/**
 * LandingV2 — full rewrite (Feb 21, 2026) using the owner-authored
 * copy verbatim. Six top-level sections, several sub-blocks inside
 * "Features & Benefits".
 *
 * Non-negotiables:
 *   • Web pitch = 30-day money-back — never the 3-day trial (iOS-only).
 *   • Real price = 247 SAR/year (backend `core.DEFAULT_CONFIG`).
 *     → ~21 SAR/month  → ~68 هللة/day.
 *   • Country resolved server-side via /api/public/geo (same-origin,
 *     no CSP surprises). Falls back to SA.
 *   • Owner-approved copy — do not paraphrase.
 */
import { Fragment, useEffect, useMemo, useRef, useState, createContext, useContext } from "react";
import VolumeHint from "@/components/VolumeHint";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Camera, Mic, Barcode, Star, Edit3, ChefHat, Sparkles, Import,
  Heart, ShoppingCart, Flame, BarChart3, Droplets, Dumbbell,
  Footprints, Scale, TrendingUp, Bot, MessageCircle, ShieldCheck,
  ChevronDown, ChevronLeft, Check, X, Users, Lock, Share2, NotebookPen, Play, Globe,
} from "lucide-react";
import Logo from "@/components/Logo";
import CountryWelcome from "@/components/CountryWelcome";
import AllInOneHub from "@/components/AllInOneHub";
import TestimonialsSection from "@/components/TestimonialsSection";
import { shouldShowCountryWelcome } from "@/lib/gulfCountry";
import {
  useCloudflareStreamPlayer,
  prefersNativeStreamControls,
  formatVideoTime,
  buildStreamIframeUrl,
} from "@/lib/cloudflareStreamPlayer";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { isNativeApp } from "@/lib/native";
import { useI18n } from "@/lib/i18n.jsx";
import { trackClick as abTrackClick, getVariant as abGetVariant, ensureVariant as abEnsureVariant } from "@/lib/abTest";
import { captureAttribution } from "@/lib/attribution";

// ═════════════════════════════════════════════════════════════════════
// CONFIG — pricing config mirrors backend/core.py DEFAULT_CONFIG
// ═════════════════════════════════════════════════════════════════════

// Web CTAs route through `/welcome` — the shared onboarding
// entry that decides whether to show the platform-agnostic welcome
// walkthrough or bounce non-native visitors straight to the quiz.
// (Historical name was `/ios/welcome`; renamed once Android was on
// the roadmap so the URL bar no longer reads as iOS-only.)
const START_ROUTE = "/welcome";

// 247 SAR/yr is the authoritative annual price in `core.py`. Other
// Gulf entries convert at the current mid-market rate rounded to a
// friendly whole number so the per-day badge still lands cleanly.
// Non-Arab regions get their own local currency + a matching per-day
// hook — USD, EUR, GBP cover ~85 % of Google traffic outside the GCC.
const PRICING = {
  SA: { currency: "SAR", yearly: 247, symbol: "ر.س",  label: "السعودية", flag: "🇸🇦" },
  AE: { currency: "AED", yearly: 240, symbol: "د.إ",  label: "الإمارات", flag: "🇦🇪" },
  KW: { currency: "KWD", yearly: 20,  symbol: "د.ك",  label: "الكويت",   flag: "🇰🇼" },
  QA: { currency: "QAR", yearly: 240, symbol: "ر.ق",  label: "قطر",      flag: "🇶🇦" },
  BH: { currency: "BHD", yearly: 25,  symbol: "د.ب",  label: "البحرين",  flag: "🇧🇭" },
  OM: { currency: "OMR", yearly: 25,  symbol: "ر.ع",  label: "عُمان",    flag: "🇴🇲" },
  EG: { currency: "EGP", yearly: 899, symbol: "ج.م",  label: "مصر",      flag: "🇪🇬" },
  // Western markets — priced natively so a US visitor doesn't see a
  // confusing SAR conversion. Numbers chosen to keep parity with the
  // Saudi price (~$66/yr ≈ $5.50/mo ≈ $0.18/day, matching our AR
  // "less than a bottle of water" tagline).
  US: { currency: "USD", yearly: 66,  symbol: "$",    label: "United States",  flag: "🇺🇸" },
  GB: { currency: "GBP", yearly: 55,  symbol: "£",    label: "United Kingdom", flag: "🇬🇧" },
  CA: { currency: "CAD", yearly: 89,  symbol: "C$",   label: "Canada",         flag: "🇨🇦" },
  AU: { currency: "AUD", yearly: 99,  symbol: "A$",   label: "Australia",      flag: "🇦🇺" },
  DE: { currency: "EUR", yearly: 60,  symbol: "€",    label: "Deutschland",    flag: "🇩🇪" },
  FR: { currency: "EUR", yearly: 60,  symbol: "€",    label: "France",         flag: "🇫🇷" },
  ES: { currency: "EUR", yearly: 60,  symbol: "€",    label: "España",         flag: "🇪🇸" },
  IT: { currency: "EUR", yearly: 60,  symbol: "€",    label: "Italia",         flag: "🇮🇹" },
  NL: { currency: "EUR", yearly: 60,  symbol: "€",    label: "Nederland",      flag: "🇳🇱" },
  // Fallback catchall for any other country → USD (handled by the
  // usePricing hook by defaulting `code = "US"` when data.country
  // isn't in PRICING).
};

// Traditional-vs-Calofay comparison (owner-authored, section 3.6).
// Each row carries both languages so `DifferentSection` can render the
// right one based on the landing lang toggle.
const COMPARE = [
  { us: "تسجيل بالصورة والصوت والباركود",         us_en: "Log by photo, voice, or barcode" },
  { us: "سعرات وماكروز ووزن وماء وتمارين وخطوات", us_en: "Calories, macros, weight, water, workouts & steps" },
  { us: "+500 وصفة عربية جاهزة",                  us_en: "500+ ready-to-cook recipes with macros" },
  { us: "استيراد وصفة من TikTok / Instagram",     us_en: "Import a recipe from TikTok or Instagram" },
  { us: "قائمة تسوق ذكية",                        us_en: "Auto-built smart shopping list" },
  { us: "مساعد AI يعرف هدفك",                     us_en: "AI coach that actually knows your goal" },
  { us: "أخصائية تغذية عبر واتساب",               us_en: "WhatsApp support team" },
  { us: "صور تقدم ومقارنة قبل وبعد",             us_en: "Before/after progress photos side-by-side" },
];

const FAQ_ITEMS = [
  // Promoted to the top of the FAQ so it's the first thing a visitor
  // sees — the NEW10 code is only valid on the website checkout and
  // doesn't exist inside the iOS/Android apps (Apple/Google don't
  // allow discount codes). Driving all discount-aware subscribers to
  // the web funnel also preserves our 15-day refund window.
  { q: "هل يوجد كود خصم للاشتراك؟",
    q_en: "Is there a discount code I can use?",
    a: (
      <>
        نعم — استخدم الكود{" "}
        <b className="bg-brand-500/15 text-brand-600 rounded px-2 py-0.5 font-black">NEW10</b> للاستفادة من{" "}
        <b>خصم خاص للمشتركين الجدد</b>.
        <br /><br />
        الكود يُطبّق تلقائيًا لما تضغط <b>«ابدأ الآن»</b>، وتقدر تتأكد منه قبل الدفع. وبالإضافة للخصم، تستفيد من <b>ضمان الاسترجاع الكامل خلال 15 يوم</b> من دون أي أسئلة.
      </>
    ),
    a_en: (
      <>
        Yes — use the code{" "}
        <b className="bg-brand-500/15 text-brand-600 rounded px-2 py-0.5 font-black">NEW10</b> to unlock a{" "}
        <b>special welcome discount for new subscribers</b>.
        <br /><br />
        The code is <b>auto-applied</b> the moment you tap "Start now", and you can double-check it before paying. On top of the discount, you also get a <b>full 15-day money-back guarantee</b>, no questions asked.
      </>
    ) },
  { q: "هل يعمل كالوفاي على الآيفون والأندرويد؟",
    q_en: "Does Calofay work on iPhone and Android?",
    a: (
      <>
        نعم — كالوفاي متوفر على الآيفون (App Store) وعلى الأندرويد (Google Play) وأيضاً من المتصفح على calofay.app. كل بياناتك تتزامن تلقائيًا بين الأجهزة، فتقدر تسجل وجبتك من الجوال وتشوف تقدمك من الكمبيوتر، وتسجل دخول بنفس البريد في أي مكان.

      </>
    ),
    a_en: (
      <>
        Yes — Calofay runs natively on iPhone (App Store) and Android (Google Play), and also in the browser at calofay.app. Your data syncs automatically across devices — log a meal on your phone, check progress on your laptop, and sign in anywhere with the same email.

      </>
    ) },
  { q: "هل كالوفاي مناسب فقط لتنزيل الوزن؟",
    q_en: "Is Calofay only for losing weight?",
    a: "لا. تقدر تستخدم كالوفاي لتنزيل الوزن أو تثبيت الوزن أو زيادته أو بناء العضل، ويتم تحديد السعرات والماكروز حسب هدفك وبياناتك.",
    a_en: "Not at all. You can use Calofay for weight loss, maintenance, gaining weight, or building muscle — your calorie and macro targets are set to whichever goal you pick." },
  { q: "كيف يحسب كالوفاي احتياجي؟",
    q_en: "How does Calofay calculate my needs?",
    a: "راح تجاوب على أسئلة مثل العمر والطول والوزن ومستوى النشاط والهدف، وبعدها يحسب كالوفاي هدفك اليومي من السعرات والماكروز.",
    a_en: "You'll answer a few quick questions — age, height, weight, activity level, goal — and Calofay dials in your daily calorie and macro targets from there." },
  { q: "هل أقدر أسجل وجبتي بالصورة؟",
    q_en: "Can I log a meal by taking a photo?",
    a: "نعم. صور وجبتك وخلي الذكاء الاصطناعي يقترح المكونات والكميات والقيم الغذائية، وبعدها راجع وعدل النتيجة قبل الحفظ.",
    a_en: "Yes. Snap a photo and our AI suggests the ingredients, portions, and nutrition — review or tweak the estimate before saving." },
  { q: "هل تحليل الصورة دقيق؟",
    q_en: "How accurate is the photo analysis?",
    a: "يعطي الذكاء الاصطناعي تقديرًا بناءً على الأطعمة والكميات الظاهرة، وقد تختلف النتيجة إذا كانت بعض المكونات أو الكميات غير واضحة. لذلك تقدر دائمًا تراجع وتعدل النتيجة قبل الحفظ.",
    a_en: "The AI gives you an estimate based on what it can see. It may be off if portions or hidden ingredients aren't obvious, which is why you can always adjust the result before it's saved." },
  { q: "هل لازم أصور كل وجبة؟",
    q_en: "Do I have to photograph every meal?",
    a: "لا. تقدر تسجل بالصورة أو الصوت أو الباركود أو من الوصفات والمفضلة أو تضيف الوجبة يدويًا.",
    a_en: "Nope. Log by photo, voice, barcode, from your favorites/recipes, or type it in manually — whichever's fastest." },
  { q: "هل توجد وصفات داخل كالوفاي؟",
    q_en: "Does Calofay come with recipes?",
    a: "نعم. داخل كالوفاي أكثر من 500 وصفة عربية جاهزة مع المكونات وطريقة التحضير والسعرات والماكروز، ويوجد فلترة حسب الوجبة والسعرات.",
    a_en: "Yes — 500+ ready-to-cook recipes with ingredients, instructions, and full macros. Filter by meal type or calorie range." },
  { q: "هل أقدر أحفظ وصفاتي الخاصة؟",
    q_en: "Can I save my own recipes?",
    a: "نعم. تقدر تنشئ وصفاتك أو تستوردها من رابط أو نص أو صورة وتحفظها داخل مكتبتك.",
    a_en: "Absolutely. Create your own or import them from a link, text, or photo — everything lives in your personal recipe library." },
  { q: "هل يوجد شخص حقيقي أقدر أتواصل معه؟",
    q_en: "Is there a real person I can talk to?",
    a: "نعم. تقدر تتواصل مع أخصائية التغذية عبر واتساب عند الحاجة.",
    a_en: "Yes — our support team answers on WhatsApp whenever you have a question about using Calofay." },
  { q: "كم سعر كالوفاي؟",
    q_en: "How much does Calofay cost?",
    a: ({ pricing }) => (
      <>
        <b>{pricing.monthly} {pricing.symbol} في الشهر</b> فقط (يُدفع سنويًا {pricing.yearly} {pricing.symbol})،
        {pricing.country === "SA"
          ? <> يعني حوالي <b>68 هللة في اليوم</b> — أقل من سعر الموية اللي تشتريها بريال.</>
          : <> يعني حوالي <b>{pricing.perDay} {pricing.symbol}</b> في اليوم فقط — أقل من كوب قهوة.</>
        }
      </>
    ),
    a_en: ({ pricing }) => {
      // In English, always render the ISO currency code (SAR, AED, USD…)
      // instead of the Arabic script symbol so US/UK visitors read a
      // familiar shape. Latin symbols ($/£/€) render as-is.
      const arabicSymbols = ["ر.س", "د.إ", "د.ك", "ر.ق", "د.ب", "ر.ع", "ج.م"];
      const sym = arabicSymbols.includes(pricing.symbol) ? pricing.currency : pricing.symbol;
      return (
        <>
          Just <b>{pricing.monthly} {sym} per month</b> (billed annually at {pricing.yearly} {sym}) —
          {pricing.country === "SA"
            ? <> that's about <b>$0.20 a day</b>, less than a bottle of water.</>
            : <> that's roughly <b>{pricing.perDay} {sym}</b> a day, less than a cup of coffee.</>
          }
        </>
      );
    } },
  { q: "ماذا لو اشتركت وما ناسبني كالوفاي؟",
    q_en: "What if I subscribe and Calofay isn't for me?",
    a: "اشتراكك مشمول بضمان استرجاع لمدة 15 يوم. إذا ما ناسبك كالوفاي خلال الفترة المحددة، تواصل معنا لطلب استرجاع المبلغ.",
    a_en: "Every subscription is covered by our 15-day money-back guarantee. If it doesn't click for you within that window, just reach out and we'll refund you." },
];

// ═════════════════════════════════════════════════════════════════════
// HOOKS
// ═════════════════════════════════════════════════════════════════════

function usePricing() {
  // Non-Arab visitors default to US pricing (USD) so they don't see
  // a Saudi Riyal number they can't parse at a glance. GCC visitors
  // still land on their own currency.
  const [code, setCode] = useState("SA");
  useEffect(() => {
    let alive = true;
    api.get("/public/geo")
      .then(({ data }) => {
        if (!alive) return;
        const c = data?.country;
        if (c && PRICING[c]) setCode(c);
        else setCode("US");  // unmapped country → USD fallback
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const cfg = PRICING[code] || PRICING.US;
  return {
    ...cfg,
    country: code,
    perDay: cfg.yearly < 100 ? (cfg.yearly / 365).toFixed(2) : (cfg.yearly / 365).toFixed(1),
    monthly: Math.round(cfg.yearly / 12),
  };
}

/**
 * Return the currency symbol to *render* for a given `pricing` object
 * and page language. In English we always render the ISO code (SAR,
 * AED, USD, GBP, EUR…) so a US/UK visitor never sees an Arabic-script
 * currency mark like "ر.س". In Arabic we keep the friendly Arabic
 * symbol. Latin currencies ($, £, €) look right in both languages, so
 * we return the raw `symbol` for those regardless of lang.
 */
function displaySymbol(pricing, lang) {
  if (lang !== "en") return pricing.symbol;
  // Arabic-script symbols → replace with the ISO code
  const arabicSymbols = ["ر.س", "د.إ", "د.ك", "ر.ق", "د.ب", "ر.ع", "ج.م"];
  if (arabicSymbols.includes(pricing.symbol)) return pricing.currency;
  return pricing.symbol;
}

function useStats() {
  const [s, setS] = useState({ users: 0, meals: 0 });
  useEffect(() => {
    let alive = true;
    api.get("/public/stats")
      .then(({ data }) => { if (alive) setS({ users: data.users || 0, meals: data.meals || 0 }); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return s;
}

// ═════════════════════════════════════════════════════════════════════
// SHARED UI
// ═════════════════════════════════════════════════════════════════════

function PillCta({ onClick, children, testid, variant = "primary" }) {
  const cls = variant === "primary"
    ? "orange-gradient text-white font-bold px-6 py-3.5 rounded-full shadow-floating"
    : "bg-white text-brand-600 border-2 border-brand-500 font-bold px-6 py-3.5 rounded-full";
  return (
    <button onClick={onClick} data-testid={testid}
      className={`${cls} inline-flex items-center gap-2 active:scale-[0.98] transition`}>
      {children} <ChevronLeft className="w-4 h-4" />
    </button>
  );
}

function CheckLine({ children }) {
  return (
    <div className="flex items-center gap-2 text-sm text-neutral-700">
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-50 text-emerald-600 shrink-0">
        <Check className="w-3 h-3" strokeWidth={3} />
      </span>
      <span>{children}</span>
    </div>
  );
}

function SectionTitle({ eyebrow, title, sub, testid }) {
  return (
    <div className="text-center mb-10 md:mb-14" data-testid={testid}>
      {eyebrow && (
        <div className="inline-flex items-center gap-1.5 bg-brand-50 text-brand-600 text-xs font-bold px-3 py-1.5 rounded-full mb-4">
          {eyebrow}
        </div>
      )}
      <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-neutral-900 leading-tight mb-3">
        {title}
      </h2>
      {sub && <p className="text-base text-neutral-600 max-w-2xl mx-auto leading-relaxed">{sub}</p>}
    </div>
  );
}

/** A bold, self-contained "bubble" that anchors each section. Holds a
 *  small eyebrow line + a large headline together inside one rounded
 *  bordered block, then an optional paragraph below it (outside the
 *  bubble). Renders in light-on-dark and dark-on-light automatically. */
function SectionBubble({ eyebrow, title, sub, dark = false, testid }) {
  const wrapCls = dark
    ? "bg-white/[0.04] backdrop-blur border-2 border-white text-white"
    : "bg-white border-2 border-neutral-900 text-neutral-900";
  const eyebrowCls = dark ? "text-brand-300" : "text-brand-600";
  const subCls = dark ? "text-white/70" : "text-neutral-600";
  return (
    <div className="text-center mb-10 md:mb-14" data-testid={testid}>
      <div className={`inline-block ${wrapCls} rounded-[2rem] px-6 md:px-10 py-6 md:py-8 max-w-3xl shadow-sm`}>
        {eyebrow && (
          <div className={`text-sm md:text-base font-bold mb-2 md:mb-3 ${eyebrowCls}`}>
            {eyebrow}
          </div>
        )}
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black leading-[1.15]">
          {title}
        </h2>
      </div>
      {sub && (
        <p className={`text-sm md:text-base ${subCls} max-w-2xl mx-auto leading-relaxed mt-5 md:mt-6`}>
          {sub}
        </p>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// SECTION WRAPPERS — light/dark alternation with orange glow
// ═════════════════════════════════════════════════════════════════════

/** Dark section: near-black base with a soft orange radial glow at the
 *  top. Cards inside should use `dark-card` (white/5 + white/10). */
function DarkSection({ children, testid }) {
  const { isRTL } = useLandingLang();
  return (
    <section id={testid} className="relative overflow-hidden py-16 md:py-20 px-4 bg-neutral-950" dir={isRTL ? "rtl" : "ltr"} data-testid={testid} style={{ scrollMarginTop: 72 }}>
      <div
        className="absolute inset-x-0 top-0 h-[520px] -z-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 800px 420px at 50% -10%, rgba(249,115,22,0.35), rgba(249,115,22,0.10) 40%, transparent 70%)",
        }}
      />
      <div
        className="absolute inset-x-0 bottom-0 h-[280px] -z-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 700px 260px at 80% 100%, rgba(249,115,22,0.18), transparent 70%)",
        }}
      />
      <div className="relative">{children}</div>
    </section>
  );
}

/** Light section: white base with soft orange gradients on top and side. */
function LightSection({ children, testid, extraClass = "" }) {
  const { isRTL } = useLandingLang();
  return (
    <section
      id={testid}
      className={`relative overflow-hidden py-16 md:py-20 px-4 bg-white ${extraClass}`}
      dir={isRTL ? "rtl" : "ltr"}
      data-testid={testid}
      style={{ scrollMarginTop: 72 }}
    >
      <div
        className="absolute inset-x-0 top-0 h-[420px] -z-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 800px 380px at 50% -10%, rgba(249,115,22,0.16), transparent 70%)",
        }}
      />
      <div
        className="absolute inset-x-0 bottom-0 h-[320px] -z-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 700px 260px at 20% 100%, rgba(249,115,22,0.10), transparent 70%)",
        }}
      />
      <div className="relative">{children}</div>
    </section>
  );
}

// ═════════════════════════════════════════════════════════════════════
// LANGUAGE CONTEXT + COPY DICTIONARY
// ═════════════════════════════════════════════════════════════════════
//
// The landing was authored 100 % Arabic. To open it to English visitors
// we scope a lightweight i18n context to this file only — the global
// i18n provider stays untouched (its `lang` follows the authed user,
// which we don't want to hijack for anonymous landing visitors).
//
// Phase 1 (this ship): Header, Hero, Guarantee, Final CTA, Footer.
// Phase 2 (next ship): FAQ, Features tabs, Competitors table.
// Sections not yet translated fall back to the Arabic string when the
// user picks EN — this is intentional so nothing looks broken during
// the phased rollout.

const LandingLangContext = createContext({ lang: "ar", setLang: () => {}, t: (_ar, en) => _ar, isRTL: true, variant: "default" });

// Landing copy dictionary. Values MUST be objects with { ar, en } so
// `t()` can transparently fall back to Arabic when English is missing.
// English copy is adapted-to-tone, NOT literally translated (per user
// direction — "بشكل يتناسب مع اللغة الانجليزية وليس شرط ترجمة حرفية").
const COPY = {
  // ── Header
  "header.login":        { ar: "دخول المشتركين",    en: "Log in" },
  "header.cta":          { ar: "اشترك الآن",          en: "Try Calofay" },
  "header.lang_toggle_to_en": { ar: "English",       en: "English" },
  "header.lang_toggle_to_ar": { ar: "العربية",       en: "العربية" },

  // ── Hero
  "hero.eyebrow":        { ar: "تطبيق السعرات والدايت المدعوم بالذكاء الاصطناعي",
                           en: "The AI-powered calorie & diet app" },
  "hero.title_line1":    { ar: "كل اللي تحتاجه لرحلة الدايت…",
                           en: "Everything you need to lose weight…" },
  "hero.title_line2":    { ar: "في تطبيق واحد",
                           en: "in one clean app." },
  "hero.subtitle":       { ar: (<>كالوفاي يحسب لك احتياجك من السعرات والماكروز حسب هدفك، ويسهّل عليك تسجيل أكلك ومتابعة وزنك ونشاطك وتقدمك — وكل هذا بتجربة بسيطة ومصممة <b className="text-neutral-900">بالعربي</b>.</>),
                           en: (<>Calofay calculates the exact calories and macros for your goal, then makes logging food, tracking weight, and staying on top of your progress ridiculously simple. Built <b className="text-neutral-900">for the way you actually eat</b>.</>) },
  "hero.cta":            { ar: "اشترك الآن",          en: "Try Calofay" },
  "hero.guarantee_note": { ar: "ضمان استرجاع 15 يوم", en: "15-day money-back guarantee" },
  "hero.bullet_1":       { ar: "يحسب لك السعرات والماكروز حسب هدفك",
                           en: "Calorie + macro targets tailored to your goal" },
  "hero.bullet_2":       { ar: "تسجيل الوجبات بالصورة أو الصوت أو الباركود",
                           en: "Log meals by photo, voice, or barcode — in seconds" },
  "hero.bullet_3":       { ar: "أكثر من 500 وصفة صحية",
                           en: "500+ healthy recipes with full macro breakdowns" },
  "hero.bullet_4":       { ar: "متابعة الوزن والتمارين والماء والخطوات",
                           en: "Weight, workouts, water & steps — one dashboard" },
  "hero.bullet_5":       { ar: "صور نفسك قبل وبعد وتابع وقارن التغيير",
                           en: "Before/after photos so you SEE the change" },
  "hero.bullet_6":       { ar: "شات ذكاء اصطناعي + أخصائية تغذية عبر واتساب",
                           en: "AI coach + WhatsApp support team" },
  "hero.bullet_7":       { ar: "ضمان استرجاع كامل لمدة 15 يوم",
                           en: "Full refund within 15 days — no questions asked" },

  // ── Hero (recipe variant — /recipe route). Overrides the calorie
  // narrative with a recipe-first pitch: 500+ ready recipes, IG/TikTok
  // imports, AI meal chat, auto shopping list. Only the labels change;
  // every downstream section (guarantee/pricing/FAQ) stays identical
  // because that's what the visitor asked for.
  "hero_r.eyebrow":      { ar: "مكتبة وصفات ذكية + استيراد من إنستقرام وتيك توك",
                           en: "Smart recipe hub + Instagram & TikTok imports" },
  "hero_r.title_line1":  { ar: "كل وصفة تحبها…",
                           en: "Every recipe you love…" },
  "hero_r.title_line2":  { ar: "في مكان واحد",
                           en: "in one place." },
  "hero_r.subtitle":     { ar: (<>أكثر من <b className="text-neutral-900">500 وصفة</b> صحية بسعرات محسوبة، استيراد وصفات إنستقرام وتيك توك بضغطة، قائمة تسوق تلقائية، وشات ذكاء اصطناعي يجاوبك عن أي وصفة أو وجبة — كله <b className="text-neutral-900">بالعربي</b>.</>),
                           en: (<>500+ healthy recipes with calculated macros, one-tap imports from Instagram & TikTok, auto-generated shopping lists, and an AI chef that answers any recipe question.</>) },
  "hero_r.cta":          { ar: "ابدأ الآن",           en: "Start now" },
  "hero_r.bullet_1":     { ar: "أكثر من 500 وصفة صحية بسعرات وماكروز محسوبة",
                           en: "500+ healthy recipes with pre-calculated calories & macros" },
  "hero_r.bullet_2":     { ar: "استورد وصفات إنستقرام وتيك توك من الرابط مباشرة",
                           en: "Import Instagram & TikTok recipes with one tap" },
  "hero_r.bullet_3":     { ar: "قائمة تسوق تلقائية لكل وصفة",
                           en: "Auto-generated shopping list for every recipe" },
  "hero_r.bullet_4":     { ar: "شات ذكاء اصطناعي يجاوبك عن الوجبات والوصفات",
                           en: "AI chef chat — answers any recipe or meal question" },
  "hero_r.bullet_5":     { ar: "احفظ وصفاتك المفضلة وأعد استخدامها بسهولة",
                           en: "Save favorites and reuse them any time" },
  "hero_r.bullet_6":     { ar: "دعم أخصائية تغذية عبر واتساب لأي استفسار",
                           en: "WhatsApp support team for any question" },
  "hero_r.bullet_7":     { ar: "ضمان استرجاع كامل لمدة 15 يوم",
                           en: "Full refund within 15 days — no questions asked" },

  // ── Guarantee
  "guar.badge":          { ar: "وعدنا الشخصي",        en: "Our personal promise" },
  "guar.headline_pre":   { ar: "ضمان استرجاع كامل لمدة ",
                           en: "Full refund within " },
  "guar.headline_days":  { ar: "15 يوم",              en: "15 days" },
  "guar.headline_sub":   { ar: "لكل المشتركين",
                           en: "For every subscriber" },
  "guar.body_1":         { ar: (<>من يوم بدأنا برمجة تطبيق كالوفاي، كان هدفنا واضح: نسوي تطبيق ينافس أفضل التطبيقات العالمية، لكن بتجربة أسهل، أذكى، <b className="text-neutral-900">وبالعربي</b>.</>),
                           en: (<>From the day we started building Calofay, our goal was clear: create an app that competes with the best in the world — but easier, smarter, and <b className="text-neutral-900">in Arabic</b>.</>) },
  "guar.body_2":         { ar: (<>نحنا واثقين إنك حتستفيد من كالوفاي أكثر من أي تطبيق جربته قبل كذا. لذلك، تقدر تجربه لمدة <b className="text-neutral-900">15 يوم</b>، وإذا ما استفدت، تواصل معنا ونرجع لك <b className="text-emerald-600">كامل المبلغ</b>.</>),
                           en: (<>We're confident you'll get more out of Calofay than any app you've tried before. So try it for <b className="text-neutral-900">15 full days</b>. If it doesn't work for you, reach out and we'll refund <b className="text-emerald-600">every penny</b>.</>) },
  "guar.body_3":         { ar: (<>وإذا كان دفعك عن طريق <b className="text-neutral-900">App Store</b>، يتم طلب الاسترجاع من خلال Apple.</>),
                           en: (<>If you paid through the <b className="text-neutral-900">App Store</b>, the refund is requested through Apple.</>) },
  "guar.body_close":     { ar: "جربه… واحكم بنفسك.",   en: "Try it — and judge for yourself." },
  "guar.saudi_pride":    { ar: "براند سعودي بكل فخر",   en: "Proudly a Saudi brand" },
  "guar.cta":            { ar: "ابدأ بدون مخاطرة",    en: "Start risk-free" },
  "guar.disclaimer":     { ar: "",
                           en: "" },

  // ── Final CTA
  "final.headline":      { ar: "جاهز تخلي الدايت أبسط؟", en: "Ready to make dieting simple?" },
  "final.sub":           { ar: "اعرف احتياجك، سجل أكلك بالطريقة اللي تناسبك، وخلي كل أدوات رحلتك في مكان واحد.",
                           en: "Know your targets, log your food the way you like, and keep your whole journey in one place." },
  "final.price_month":   { ar: "/ شهر", en: "/ month" },
  "final.price_year":    { ar: "يُدفع سنويًا",         en: "billed annually at" },
  "final.perday_sa":     { ar: "حوالي 68 هللة باليوم — أقل من سعر الموية اللي تشتريها بريال.",
                           en: "That's about $0.20 a day — less than a bottle of water." },
  "final.cta":           { ar: "اشترك الآن",           en: "Get Calofay" },
  "final.guarantee":     { ar: "ضمان استرجاع كامل لمدة 15 يوم",
                           en: "15-day full money-back guarantee" },

  // ── Footer
  "footer.tagline":      { ar: "كالوفاي — تطبيق السعرات والدايت المدعوم بالذكاء الاصطناعي. سجل وجباتك بالصورة أو الصوت أو الباركود، وتابع وزنك وسعراتك في مكان واحد.",
                           en: "Calofay — the AI-powered calorie & diet app. Log meals by photo, voice, or barcode, and track your weight & macros — all in one place." },
  "footer.support":      { ar: "دعم العملاء",          en: "Customer support" },
  "footer.whatsapp":     { ar: "واتساب الدعم",         en: "WhatsApp support" },
  "footer.login":        { ar: "تسجيل دخول المشتركين", en: "Member login" },
  "footer.col_content":  { ar: "المحتوى",              en: "Content" },
  "footer.blog":         { ar: "مدونة كالوفاي",         en: "Calofay Blog" },
  "footer.quiz":         { ar: "ابدأ اختبار السعرات",   en: "Take the calorie quiz" },
  "footer.pricing":      { ar: "الأسعار",              en: "Pricing" },
  "footer.col_legal":    { ar: "القانوني",             en: "Legal" },
  "footer.terms":        { ar: "الشروط والأحكام",       en: "Terms of Service" },
  "footer.privacy":      { ar: "سياسة الخصوصية",       en: "Privacy Policy" },
  "footer.health":       { ar: "إخلاء المسؤولية الصحي", en: "Health Disclaimer" },
  "footer.methodology":  { ar: "منهجية كالوفاي",        en: "How we calculate everything" },
  "footer.col_refund":   { ar: "الاسترجاع والاشتراك",   en: "Refunds & subscriptions" },
  "footer.refund_web":   { ar: (<>ضمان استرجاع كامل خلال <b>15 يوم</b> لكل المشتركين — راسلنا وبنرجّع كامل المبلغ.</>),
                           en: (<>Full <b>15-day money-back guarantee</b> for every subscriber — email us and we refund in full.</>) },
  "footer.refund_ios":   { ar: (<><b className="text-white">الدفع عبر App Store:</b> يتم طلب الاسترجاع من خلال Apple.</>),
                           en: (<><b className="text-white">Paid through the App Store:</b> the refund is requested through Apple.</>) },
  "footer.copyright":    { ar: "جميع الحقوق محفوظة.", en: "All rights reserved." },
  "footer.made_love":    { ar: "صنع بحب في السعودية 🇸🇦",
                           en: "Made with love in Saudi Arabia 🇸🇦" },

  // ── Problem/Solution
  "ps.eyebrow":          { ar: "أكبر مشكلة في الدايت؟", en: "The biggest problem with dieting?" },
  "ps.title":            { ar: "إنك ما تعرف فعلًا وش قاعد يصير", en: "You don't actually know what's going on" },
  "ps.sub":              { ar: "ممكن تقلل أكلك، تختار وجبات صحية وتتمرن… لكن إذا ما تعرف كم سعرة أكلت وكم يحتاج جسمك فعلًا، يصير صعب تعرف هل أنت ماشي تجاه هدفك أو لا.",
                           en: "You can eat less, pick healthier meals, and hit the gym — but without knowing what you actually ate and what your body needs, it's a guess whether you're heading toward your goal or not." },
  // Recipe-variant overrides — same section, different narrative:
  // "cooking healthy is exhausting because recipes are scattered".
  "ps.eyebrow__recipe":  { ar: "أكبر مشكلة في الطبخ الصحي؟",
                           en: "The biggest problem with healthy cooking?" },
  "ps.title__recipe":    { ar: "وصفاتك متفرقة وتسوقك يستهلك يومك",
                           en: "Your recipes are scattered and shopping eats your day" },
  "ps.sub__recipe":      { ar: "تحفظ وصفة في إنستقرام، وثانية في تيك توك، وثالثة في كتاب — وكل مرة تبغى تطبخ تضيّع وقت بالبحث وتجهيز التسوق. ما عندك مكان واحد يجمع كل شيء.",
                           en: "One recipe saved on Instagram, another on TikTok, a third in a cookbook — every time you cook, you waste time hunting for it and planning the shopping list. You don't have a single place that holds it all." },
  "ps.close_1__recipe":  { ar: "بدل ما تدور وتخطط وتنسى…",
                           en: "Instead of hunting, planning, and forgetting…" },
  "ps.close_2__recipe":  { ar: "كالوفاي يجمع وصفاتك ويجهّز قائمة تسوقك في مكان واحد.",
                           en: "Calofay puts your recipes and shopping list in one place." },
  "ps.solution_badge":   { ar: "الوضوح الكامل · مع كالوفاي تعرف:",
                           en: "Full clarity — with Calofay you know:" },
  "ps.close_1":          { ar: "بدل ما تخمّن إذا أنت ماشي صح…",
                           en: "Instead of guessing whether you're on track…" },
  "ps.close_2":          { ar: "كالوفاي يخلي أرقامك وتقدمك قدامك كل يوم.",
                           en: "Calofay puts your numbers and progress in front of you — every day." },
  "ps.cta":              { ar: "اشترك الآن",              en: "Get Calofay" },

  // ── Features Hub
  "feat.eyebrow":        { ar: "المميزات والفوائد",        en: "Features & benefits" },
  "feat.title":          { ar: "15 ميزة كاملة داخل كالوفاي", en: "15 features. One app." },
  "feat.sub":            { ar: "بدل ما تستخدم أكثر من تطبيق عشان تتابع أكلك ووصفاتك ووزنك وتقدمك… كل هذي المميزات موجودة داخل كالوفاي.",
                           en: "Stop juggling three or four apps — food, recipes, weight, progress. It's all inside Calofay." },
  "feat.number_label":   { ar: "الميزة",                    en: "Feature" },

  // ── Different section (compare vs traditional)
  "diff.eyebrow":        { ar: "لماذا كالوفاي؟",           en: "Why Calofay?" },
  "diff.title":          { ar: "الفرق بين كالوفاي وتطبيق سعرات تقليدي",
                           en: "Calofay vs. a traditional calorie app" },
  "diff.sub":            { ar: "نظرة سريعة على كل شي راح تلاقيه في كالوفاي ومو موجود في أي تطبيق سعرات تقليدي.",
                           en: "A quick look at everything you get in Calofay that isn't in a standard calorie tracker." },
  "diff.col_feature":    { ar: "الميزة",                    en: "Feature" },
  "diff.col_traditional":{ ar: "تطبيق تقليدي",              en: "Traditional app" },
  "diff.close_1":        { ar: "مو مجرد تطبيق لحساب السعرات.",
                           en: "More than a calorie counter." },
  "diff.close_2":        { ar: "كالوفاي يجمع رحلتك كاملة في مكان واحد.",
                           en: "Your entire journey — one clean app." },
  "diff.cta":            { ar: "اشترك الآن",              en: "Get started" },

  // ── How to Start
  "how.eyebrow":         { ar: "ابدأ دايتك بدون حوسة",      en: "Start without the noise" },
  "how.title":           { ar: "كالوفاي يحسبها لك من أول يوم",
                           en: "Calofay does the math from day one" },
  "how.cta":             { ar: "اشترك الآن",              en: "Start now" },
  // Recipe variant — swaps title so the "3 steps" section reads as
  // "3 steps to your next favourite recipe".
  "how.eyebrow__recipe": { ar: "ابدأ الطبخ بدون حوسة",
                           en: "Cook without the noise" },
  "how.title__recipe":   { ar: "٣ خطوات وتلقى وصفة اليوم",
                           en: "3 steps to today's recipe" },

  // ── Price / Value
  "price.eyebrow":       { ar: "السعر والقيمة",             en: "Pricing" },
  "price.title":         { ar: "15 ميزة في اشتراك واحد",    en: "15 features. One subscription." },
  "price.sub":           { ar: "بدل ما تستخدم أكثر من تطبيق عشان تتابع أكلك ووصفاتك وتمارينك ووزنك وتقدمك… كل شيء موجود داخل كالوفاي.",
                           en: "One subscription replaces the pile of apps you'd use for food, recipes, workouts, weight, and progress." },
  "price.title__recipe": { ar: "مكتبة وصفاتك في اشتراك واحد",
                           en: "Your recipe library. One subscription." },
  "price.sub__recipe":   { ar: "500+ وصفة جاهزة، استيراد إنستقرام وتيك توك، قائمة تسوق تلقائية، شات AI للطبخ — كل شيء داخل كالوفاي.",
                           en: "500+ ready recipes, Instagram & TikTok imports, auto shopping lists, and an AI chef — all inside Calofay." },
  "price.premium":       { ar: "PREMIUM · فاخر",           en: "PREMIUM" },
  "price.plan_label":    { ar: "اشتراك كالوفاي السنوي",     en: "Calofay annual plan" },
  "price.per_month":     { ar: "/ شهر",                    en: "/ month" },
  "price.yearly_pre":    { ar: "يُدفع سنويًا · ",           en: "Billed annually · " },
  "price.yearly_per_year":{ ar: " / السنة",                en: " / year" },
  "price.perday_sa":     { ar: " · حوالي 68 هللة باليوم",  en: " · about $0.20 a day" },
  "price.features_head": { ar: "وش يشمل الاشتراك",         en: "What's included" },
  "price.feature_count": { ar: "ميزة",                    en: "features" },
  "price.cta":           { ar: "اشترك في كالوفاي الآن",   en: "Get Calofay now" },
  "price.footnote_pre":  { ar: "جرّب كالوفاي براحتك — إذا ما ناسبك خلال أول ",
                           en: "Try Calofay risk-free — if it isn't for you within the first " },
  "price.footnote_days": { ar: "15 يوم",                   en: "15 days" },
  "price.footnote_post": { ar: "، تقدر تطلب استرجاع كامل.",
                           en: ", we'll refund every penny." },
  "price.trust_pay":     { ar: "دفع آمن",                  en: "Secure payment" },
  "price.trust_guar":    { ar: "ضمان 15 يوم",              en: "15-day guarantee" },
  "price.trust_support": { ar: "دعم عند الحاجة",           en: "Support when you need it" },

  // ── Competitors table
  "riv.eyebrow":         { ar: "⭐ المقارنة الحقيقية",       en: "⭐ The honest comparison" },
  "riv.title":           { ar: "مميزات ما راح تلقاها عند المنافسين",
                           en: "Features you won't find in the competition" },
  "riv.sub":             { ar: (<>مقارنة مباشرة مع أشهر تطبيقين للسعرات في العالم — <b className="text-neutral-900">MyFitnessPal</b> و <b className="text-neutral-900">Lose It!</b> — وشوف بنفسك ليش كالوفاي أفضل.</>),
                           en: (<>A direct comparison with the two biggest calorie apps in the world — <b className="text-neutral-900">MyFitnessPal</b> and <b className="text-neutral-900">Lose It!</b> — see for yourself why Calofay wins.</>) },
  "riv.col_feature":     { ar: "الميزة",                    en: "Feature" },
  "riv.best_badge":      { ar: "الأفضل",                   en: "Best" },
  "riv.close":           { ar: "10 مميزات أساسية موجودة عندنا وحدنا.",
                           en: "10 essential features only Calofay ships." },
  "riv.cta":             { ar: "اشترك الآن",              en: "Choose Calofay" },

  // ── FAQ
  "faq.eyebrow":         { ar: "الأسئلة الشائعة",           en: "FAQ" },
  "faq.title":           { ar: "أسئلة ممكن تكون في بالك",   en: "Questions you might have" },

  // ── Misc (hero video pills, floating chips, error hint)
  "video.watch_full":    { ar: "شاهد جولة كاملة داخل التطبيق",
                           en: "Watch a full tour inside the app" },
  "video.watch_now":     { ar: "شاهد الآن · شرح كامل",     en: "Play now · full walk-through" },
  "video.error":         { ar: "تعذر تشغيل الفيديو، حاول مرة أخرى",
                           en: "Couldn't play the video, try again" },
  "video.aria_pause":    { ar: "إيقاف مؤقت",               en: "Pause" },
  "video.aria_seek":     { ar: "التقديم في الفيديو",       en: "Video seek bar" },
  "video.aria_play":     { ar: "شاهد جولة داخل التطبيق",   en: "Play the app tour" },
  "video.alt_poster":    { ar: "جولة داخل تطبيق كالوفاي",  en: "Calofay app tour" },
  "chip.ai_ar":          { ar: "مساعد AI عربي",            en: "AI coach in your pocket" },
  "chip.all_ar":         { ar: "كل شي بالعربي",            en: "Every screen, thoughtfully designed" },
};

function useLandingLang() { return useContext(LandingLangContext); }
export { useLandingLang };

/**
 * Elegant language switcher — a glass-morphism pill sitting inside the
 * black header. Uses a Globe icon and shows the OPPOSITE language as
 * its label (i.e. currently browsing AR → shows "English", vice versa)
 * so the affordance is obvious in one glance.
 */
function LanguageSelector() {
  const { lang, setLang } = useLandingLang();
  const otherLang = lang === "ar" ? "en" : "ar";
  const label = lang === "ar" ? "English" : "العربية";
  return (
    <button
      type="button"
      onClick={() => setLang(otherLang)}
      className="group inline-flex items-center gap-1.5 px-3 h-9 rounded-full border border-white/20 bg-white/[0.06] text-white/95 text-[12px] font-semibold tracking-wide backdrop-blur-md hover:bg-white/[0.14] hover:border-white/40 active:scale-[0.97] transition-all shadow-[0_2px_10px_-4px_rgba(0,0,0,0.5)]"
      aria-label={lang === "ar" ? "Switch to English" : "التبديل إلى العربية"}
      data-testid="lv2-lang-toggle"
    >
      <Globe className="w-3.5 h-3.5 opacity-80 group-hover:opacity-100 transition-opacity" strokeWidth={2.2} />
      <span>{label}</span>
    </button>
  );
}

// ═════════════════════════════════════════════════════════════════════
// HEADER
// ═════════════════════════════════════════════════════════════════════

// Thin promo bar sitting ABOVE the main sticky header. Announces the
// NEW10 code (10% off) which is auto-applied at checkout via the
// `calofay_affiliate_code` localStorage seed in LandingV2's root. Only
// shown on web (not native), bilingual, dismissible per-session so
// returning visitors who already dismissed it don't see it again.
function PromoBar() {
  const { lang } = useLandingLang();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("calofay:promo:new10:dismissed") === "1"; } catch { return false; }
  });
  if (dismissed || isNativeApp()) return null;
  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem("calofay:promo:new10:dismissed", "1"); } catch {}
  };
  const copy = lang === "en" ? (
    <>🎟️ A <b>special welcome discount</b> for new subscribers — use code <b className="bg-white/20 rounded px-1.5 py-0.5 mx-1 font-black tracking-wider">NEW10</b> at checkout</>
  ) : (
    <>🎟️ خصم خاص للمشتركين الجدد — استخدم الكود <b className="bg-white/20 rounded px-1.5 py-0.5 mx-1 font-black tracking-wider">NEW10</b> عند الدفع</>
  );
  return (
    <div className="orange-gradient text-white text-[12.5px] md:text-[13px] font-medium relative" data-testid="lv2-promo-bar">
      <div className="max-w-6xl mx-auto px-10 py-2 text-center leading-relaxed">
        {copy}
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-1/2 -translate-y-1/2 end-2 w-7 h-7 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center active:scale-95 transition"
        aria-label={lang === "en" ? "Dismiss" : "إخفاء"}
        data-testid="lv2-promo-dismiss"
      >
        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
          <line x1="6" y1="6" x2="18" y2="18" />
          <line x1="18" y1="6" x2="6" y2="18" />
        </svg>
      </button>
    </div>
  );
}

function Header({ onStart }) {
  const { t, isRTL, lang } = useLandingLang();
  return (
    <header className="sticky top-0 z-40 bg-black border-b border-white/10 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)]">
      <PromoBar />
      <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between" dir={isRTL ? "rtl" : "ltr"}>
        {/* Logo: uses the Arabic wordmark on `/ar` and switches to the
            English mark + "Calofay" text on `/en` so English visitors
            see a Latin-alphabet brand at first glance. Both variants
            keep the dark-nav treatment via inline text override. */}
        {lang === "en" ? (
          <div className="inline-flex items-center gap-2 select-none" data-testid="calofay-logo">
            <img src="/calofay-mark.webp" alt="" style={{ height: 44, width: 44 }} className="rounded-[22%]" draggable={false} />
            <span className="font-black tracking-tight text-white" style={{ fontSize: 26, letterSpacing: "-0.02em" }}>Calofay</span>
          </div>
        ) : (
          <img
            src="/calofay-logo-dark.webp"
            alt="Calofay"
            style={{ height: 56, width: "auto" }}
            className="select-none pointer-events-none"
            draggable={false}
            data-testid="calofay-logo"
          />
        )}
        <div className="flex items-center gap-2">
          <LanguageSelector />
        </div>
      </div>
    </header>
  );
}

// ═════════════════════════════════════════════════════════════════════
// 1 · HERO
// ═════════════════════════════════════════════════════════════════════

const HERO_BULLET_KEYS = [
  "hero.bullet_1", "hero.bullet_2", "hero.bullet_3",
  "hero.bullet_4", "hero.bullet_5", "hero.bullet_6", "hero.bullet_7",
];
const HERO_BULLET_KEYS_RECIPE = [
  "hero_r.bullet_1", "hero_r.bullet_2", "hero_r.bullet_3",
  "hero_r.bullet_4", "hero_r.bullet_5", "hero_r.bullet_6", "hero_r.bullet_7",
];

function Hero({ onStart, variant = "default" }) {
  const { t, isRTL } = useLandingLang();
  const isRecipe = variant === "recipe";
  // Recipe variant swaps the eyebrow / title / subtitle / bullets to a
  // recipe-first narrative but keeps the exact same layout, video, and
  // guarantee note so the funnel structure is identical downstream.
  const K = isRecipe
    ? { eyebrow: "hero_r.eyebrow", t1: "hero_r.title_line1", t2: "hero_r.title_line2",
        subtitle: "hero_r.subtitle", cta: "hero_r.cta", bullets: HERO_BULLET_KEYS_RECIPE }
    : { eyebrow: "hero.eyebrow", t1: "hero.title_line1", t2: "hero.title_line2",
        subtitle: "hero.subtitle", cta: "hero.cta", bullets: HERO_BULLET_KEYS };
  return (
    <section className="relative overflow-hidden py-14 md:py-20 px-4" dir={isRTL ? "rtl" : "ltr"} data-testid="lv2-hero">
      {/* Layered warm background */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-brand-50 via-white to-white" />
      <div
        aria-hidden
        className="absolute -top-32 -right-40 w-[520px] h-[520px] rounded-full pointer-events-none -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(circle, rgba(249,115,22,0.22), rgba(249,115,22,0) 65%)",
        }}
      />
      <div
        aria-hidden
        className="absolute top-40 -left-32 w-[420px] h-[420px] rounded-full pointer-events-none -z-10 opacity-60"
        style={{
          background:
            "radial-gradient(circle, rgba(251,191,36,0.18), rgba(251,191,36,0) 65%)",
        }}
      />

      <div className="max-w-6xl mx-auto grid md:grid-cols-[1.15fr_1fr] gap-10 md:gap-14 items-center">
        {/* Copy column */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 bg-white/90 backdrop-blur border-2 border-brand-200 text-brand-600 text-[11px] md:text-xs font-black px-3.5 py-1.5 rounded-full mb-5 shadow-sm">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />
            {t(K.eyebrow)}
          </div>

          {/* Title */}
          <h1
            className="text-[28px] leading-[1.2] sm:text-4xl md:text-[46px] md:leading-[1.15] font-black text-neutral-900 mb-5 tracking-tight"
            data-testid="lv2-hero-title"
          >
            {t(K.t1)}
            <br />
            <span className="relative inline-block mt-1">
              <span className="relative z-10 text-brand-500">{t(K.t2)}</span>
              <span
                aria-hidden
                className="absolute right-0 left-0 bottom-1 h-3 md:h-4 bg-brand-100/80 -z-0 rounded-sm"
              />
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-[15px] sm:text-base md:text-lg text-neutral-600 leading-relaxed mb-6 max-w-xl">
            {t(K.subtitle)}
          </p>
        </motion.div>

        {/* Phone mockup / English slider column */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <HeroVisual />
        </motion.div>
      </div>

      {/* ── "7 apps in 1" orbital hub — sits between the hero grid and
             the feature bullets so visitors immediately see the all-in-one
             value prop before scrolling further. */}
      <AllInOneHub />

      {/* ── Feature bullets + primary CTA — sit BELOW the video on
             every viewport (mobile stack: copy → video → bullets → CTA;
             desktop: copy | video, then bullets + CTA centered
             underneath). Moved out of the left column at the user's
             request so the video breathes and the CTA is the final,
             most-committed thing on screen. */}
      <div className="max-w-2xl mx-auto mt-2 md:mt-4 px-2" data-testid="lv2-hero-features">
        <ul className="space-y-3 mb-7">
          {K.bullets.map((key, i) => (
            <motion.li
              key={key}
              initial={{ opacity: 0, x: 12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.35, delay: i * 0.05, ease: "easeOut" }}
              className="flex items-start gap-3 text-[15px] sm:text-base text-neutral-800 leading-snug"
            >
              <span className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full orange-gradient text-white mt-0.5">
                <Check className="w-3.5 h-3.5" strokeWidth={4} />
              </span>
              <span>{t(key)}</span>
            </motion.li>
          ))}
        </ul>
        <div className="flex flex-col items-center gap-2">
          <PillCta onClick={onStart} testid="lv2-hero-cta">
            {t(K.cta)}
          </PillCta>
          <div className="flex items-center gap-1.5 text-xs text-neutral-500">
            <ShieldCheck className="w-4 h-4 text-emerald-500" strokeWidth={2.5} />
            <span>{t("hero.guarantee_note")}</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/** iPhone 14 Pro frame with an **inline tutorial video** (Cloudflare
 *  Stream-hosted, HLS-transcoded, iOS-Safari-friendly). Video does NOT
 *  autoplay — we show a Stream-generated poster + a large orange play
 *  button. The user must tap to start playback (this is a hard Facebook
 *  Ads requirement + prevents burning users' cellular data). Once
 *  tapped, we control the Stream iframe via its official SDK so tapping
 *  the orange button a second time actually PAUSES the video (and
 *  reveals the play button again). Sound is on from the first tap — the
 *  click satisfies iOS Safari's user-gesture requirement. */
const HERO_TUTORIAL_VIDEO_ID = "60c41bccbef0031e42305343d1023bd7";
const HERO_TUTORIAL_SUBDOMAIN = "customer-4ks14eaibvwflhwd.cloudflarestream.com";
const HERO_TUTORIAL_POSTER = `https://${HERO_TUTORIAL_SUBDOMAIN}/${HERO_TUTORIAL_VIDEO_ID}/thumbnails/thumbnail.jpg?time=1s&height=1200`;
const HERO_TUTORIAL_IFRAME = buildStreamIframeUrl({
  videoId: HERO_TUTORIAL_VIDEO_ID,
  subdomain: HERO_TUTORIAL_SUBDOMAIN,
});
const HERO_TUTORIAL_IFRAME_NATIVE = buildStreamIframeUrl({
  videoId: HERO_TUTORIAL_VIDEO_ID,
  subdomain: HERO_TUTORIAL_SUBDOMAIN,
  native: true,
});

// Callback ref that also stores the element in state — this is the only
// reliable way to trigger a React effect when a conditionally-rendered
// child (our iframe, mounted only after the first user tap) shows up.
// A `useRef` alone does NOT trigger re-runs, which is why the SDK
// previously never loaded (iframe was null on mount, effect ran once,
// never fired again). See `useCloudflareStreamPlayer` in
// `/app/frontend/src/lib/cloudflareStreamPlayer.js`.
function HeroVisual() {
  // English visitors see a compact horizontal slider through the 10
  // in-app English onboarding screens (they already exist as
  // /onboarding/en/1..10.webp). Arabic visitors keep the interactive
  // Cloudflare Stream tutorial video since we recorded it in Arabic.
  const { lang } = useLandingLang();
  if (lang === "en") return <EnglishHeroSlider />;
  return <DashboardMockup />;
}

/**
 * Horizontal auto-advancing slider of 10 English in-app screens, framed
 * inside an iPhone-ish rounded shape so it visually matches the Arabic
 * DashboardMockup that occupies the same slot. Uses simple state + a
 * timer (no external carousel dep) — swipeable on touch via a light
 * transform + timer reset. Dots below track position + let users jump.
 */
function EnglishHeroSlider() {
  // 5 curated English in-app screens (Home → AI Coach → Recipes →
  // Progress → Settings) uploaded by product to replace the previous
  // 10-slide walkthrough. Kept in strict 1→5 order so the story arc
  // (arrive → chat → cook → track → own the app) reads naturally.
  const slides = [1, 2, 3, 4, 5];
  const [idx, setIdx] = useState(0);
  // Auto-advance every 3.5s. Pause while the user is actively
  // interacting (touch/mouse over) so a slow reader isn't yanked
  // mid-glance. Timer restarts whenever the visible index changes.
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return undefined;
    const t = setTimeout(() => setIdx((i) => (i + 1) % slides.length), 3500);
    return () => clearTimeout(t);
  }, [idx, paused]); // eslint-disable-line react-hooks/exhaustive-deps
  const touchStart = useRef(null);
  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; setPaused(true); };
  const onTouchEnd = (e) => {
    const start = touchStart.current;
    if (start == null) return;
    const dx = e.changedTouches[0].clientX - start;
    if (Math.abs(dx) > 40) {
      setIdx((i) => (dx < 0 ? (i + 1) % slides.length : (i - 1 + slides.length) % slides.length));
    }
    touchStart.current = null;
    setTimeout(() => setPaused(false), 3000);
  };
  return (
    <div className="relative flex flex-col items-center justify-center py-2 sm:py-4" dir="ltr">
      <div className="relative flex items-center justify-center">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-72 h-72 rounded-full bg-brand-300/50 blur-[80px]" />
        </div>
        <div
          className="relative select-none"
          style={{
            width: "280px", height: "570px", borderRadius: "48px", padding: "10px",
            background: "linear-gradient(145deg, #1f1f22 0%, #0a0a0b 55%, #1a1a1c 100%)",
            boxShadow: "0 30px 60px -20px rgba(0,0,0,0.55), 0 15px 35px -10px rgba(249,115,22,0.25), inset 0 0 0 1.5px rgba(255,255,255,0.08)",
          }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <span aria-hidden className="absolute top-24 -right-[3px] w-[3px] h-8 rounded-l bg-neutral-800" />
          <span aria-hidden className="absolute top-40 -left-[3px] w-[3px] h-6 rounded-r bg-neutral-800" />
          <span aria-hidden className="absolute top-52 -left-[3px] w-[3px] h-12 rounded-r bg-neutral-800" />
          <span aria-hidden className="absolute top-72 -left-[3px] w-[3px] h-12 rounded-r bg-neutral-800" />
          <div className="relative w-full h-full overflow-hidden bg-black" style={{ borderRadius: "38px" }}>
            {slides.map((n, i) => (
              <img
                key={n}
                src={`/hero-en/${n}.webp`}
                alt={`Calofay in-app screen ${n}`}
                draggable={false}
                loading={i === 0 ? "eager" : "lazy"}
                className="absolute inset-0 w-full h-full object-cover object-center select-none transition-opacity duration-500"
                style={{ opacity: i === idx ? 1 : 0 }}
              />
            ))}
          </div>
        </div>
        {/* Floating chips — same slot the AR version uses */}
        <div className="absolute -top-3 -right-3 hidden md:flex items-center gap-1.5 bg-white rounded-full shadow-floating px-3 py-2 border-2 border-neutral-900 rotate-[-6deg] z-40">
          <Bot className="w-3.5 h-3.5 text-brand-500" />
          <span className="text-[11px] font-bold text-neutral-900 whitespace-nowrap">AI coach in your pocket</span>
        </div>
        <div className="absolute -bottom-3 -left-3 hidden md:flex items-center gap-1.5 bg-neutral-900 rounded-full shadow-floating px-3 py-2 border-2 border-white rotate-[5deg] z-40">
          <Sparkles className="w-3.5 h-3.5 text-brand-400" />
          <span className="text-[11px] font-bold text-white whitespace-nowrap">Every screen, thoughtfully designed</span>
        </div>
      </div>
      {/* Slide dots — clickable, mirror current index. */}
      <div className="mt-5 flex items-center gap-1.5" data-testid="en-hero-dots">
        {slides.map((n, i) => (
          <button
            key={n}
            type="button"
            onClick={() => { setIdx(i); setPaused(true); setTimeout(() => setPaused(false), 4000); }}
            aria-label={`Show screen ${n}`}
            className={`h-1.5 rounded-full transition-all ${
              i === idx ? "w-6 bg-brand-500" : "w-1.5 bg-neutral-300 hover:bg-neutral-400"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function DashboardMockup() {
  const { t, lang } = useLandingLang();
  // 4-state UI: "poster" (initial), "loading" (mounting iframe, waiting
  // for the real 'play' event so a failed load can never leave the user
  // stranded), "playing", "paused". The orange button is visible in
  // `poster` + `paused`; a spinner overlays in `loading`; a transparent
  // tap-target sits on top of the iframe while `playing` so tapping the
  // video toggles pause.
  const [state, setState] = useState("poster");
  // `hasError`: sticky flag flipped by the SDK error listener while
  // in the `loading` state. Surfaces a visible Arabic hint so a
  // repeatedly-failing video isn't indistinguishable from a broken
  // button (e.g. codec/network issues on some Android WebViews).
  const [hasError, setHasError] = useState(false);
  // State-backed callback ref: setting this via `ref={setIframeEl}`
  // triggers the SDK effect the moment the iframe mounts (which only
  // happens AFTER the first tap). A plain useRef would leave the SDK
  // uninitialised forever — see comment on `useCloudflareStreamPlayer`.
  const [iframeEl, setIframeEl] = useState(null);
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);
  // Sticky fallback: our custom play was blocked (autoplay policy / slow SDK) →
  // show Cloudflare's native controls so the user's tap lands inside the iframe.
  const [nativeMode, setNativeMode] = useState(() => prefersNativeStreamControls());
  const goNative = () => { setHasError(false); setNativeMode(true); setState("paused"); };
  // Safari/iOS: mount Cloudflare's player right away so ONE tap on its own play button works.
  useEffect(() => { if (nativeMode && stateRef.current === "poster") setState("paused"); }, [nativeMode]);
  // Scrubber state — kept in sync with Stream via `timeupdate` +
  // `loadedmetadata` events. `isSeeking` freezes the timeupdate sync
  // while the user is dragging the range input so the thumb doesn't
  // jump back to the last-reported position mid-drag. We MUST read the
  // flag through a ref inside the SDK callback (which was bound once
  // when the iframe mounted) — a plain useState value would be trapped
  // in a stale closure and always look `false`, defeating the freeze.
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const isSeekingRef = useRef(false);

  const player = useCloudflareStreamPlayer(iframeEl, {
    onPlay: () => setState("playing"),
    onPause: () => setState((s) => (s === "poster" ? "poster" : "paused")),
    // Only treat an error as fatal while we're still waiting for
    // playback to start. A transient error mid-playback (network
    // hiccup) shouldn't wipe the iframe and reset progress — Stream
    // will recover on its own.
    onError: () => {
      if (stateRef.current === "loading") goNative();
    },
    onTimeUpdate: (t) => { if (!isSeekingRef.current) setCurrentTime(t); },
    onLoadedMetadata: (d) => setDuration(d),
  });

  const start = async () => {
    // Mount the iframe + show a spinner. State only flips to "playing"
    // once Stream fires the real 'play' event (via onPlay -> setState),
    // guaranteeing the pause overlay never covers a stalled black box.
    setHasError(false);
    setState("loading");
    // Watchdog: if neither `play` nor `error` fires in 12s (SDK stuck,
    // network dead) escape from the spinner and surface the retry
    // hint so the user isn't watching an infinite loader.
    setTimeout(() => {
      if (stateRef.current === "loading") goNative();
    }, 5000);
    setTimeout(async () => {
      if (!player) return; // autoplay=true handles this; watchdog covers a block
      try {
        player.muted = false;
        await player.play();
      } catch (_) {
        goNative();
      }
    }, 60);
  };

  const togglePause = async () => {
    if (!player) return;
    try {
      if (player.paused) { player.muted = false; await player.play(); }
      else { player.pause(); }
    } catch { goNative(); }
  };

  return (
    <div className="relative flex flex-col items-center justify-center py-2 sm:py-4">
      {/* "Watch the full tutorial" pill + curved hand-drawn arrow
          pointing down at the phone — visible on all viewports. */}
      <div className="flex flex-col items-center mb-2 sm:mb-3 pointer-events-none">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-brand-500 to-brand-600 text-white text-xs sm:text-sm font-bold px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-floating border-2 border-white -rotate-[3deg] animate-bounce-slow"
             data-testid="hero-tutorial-cta">
          <Play className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-white" />
          <span>{t("video.watch_full")}</span>
        </div>
        <svg width="60" height="50" viewBox="0 0 70 60" fill="none" className="mt-1 -mb-1 sm:w-[70px] sm:h-[60px]">
          <path d="M12 6 C 22 20, 35 22, 40 34 C 44 44, 50 48, 55 52"
                stroke="#f97316" strokeWidth="3" strokeLinecap="round" fill="none" />
          <path d="M55 52 L 48 47 M55 52 L 51 44"
                stroke="#f97316" strokeWidth="3" strokeLinecap="round" fill="none" />
        </svg>
      </div>

      <div className="relative flex items-center justify-center">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-72 h-72 rounded-full bg-brand-300/50 blur-[80px]" />
        </div>
        <div
          className="relative"
          style={{
            width: "280px", height: "570px", borderRadius: "48px", padding: "10px",
            background: "linear-gradient(145deg, #1f1f22 0%, #0a0a0b 55%, #1a1a1c 100%)",
            boxShadow: "0 30px 60px -20px rgba(0,0,0,0.55), 0 15px 35px -10px rgba(249,115,22,0.25), inset 0 0 0 1.5px rgba(255,255,255,0.08)",
          }}
        >
          <span aria-hidden className="absolute top-24 -right-[3px] w-[3px] h-8 rounded-l bg-neutral-800" />
          <span aria-hidden className="absolute top-40 -left-[3px] w-[3px] h-6 rounded-r bg-neutral-800" />
          <span aria-hidden className="absolute top-52 -left-[3px] w-[3px] h-12 rounded-r bg-neutral-800" />
          <span aria-hidden className="absolute top-72 -left-[3px] w-[3px] h-12 rounded-r bg-neutral-800" />
          <span aria-hidden className="absolute top-32 -right-[3px] w-[3px] h-16 rounded-l bg-neutral-800" />

          <div className="relative w-full h-full overflow-hidden bg-black" style={{ borderRadius: "38px" }}>
            {/* Poster stays until the SDK confirms the first `play` event,
                which is when we flip state → "playing". This keeps the
                thumbnail visible while the SDK boots + video buffers.
                Once the user pauses, we hide the poster so they see the
                current frame under the transparent overlay. */}
            {(state === "poster" || state === "loading") && (
              <img
                src={HERO_TUTORIAL_POSTER}
                alt={t("video.alt_poster")}
                loading="eager"
                draggable={false}
                className="absolute inset-0 w-full h-full object-cover object-center select-none z-10"
              />
            )}
            {(state === "poster" || state === "loading") && (
              <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/25 to-black/40 z-10" />
            )}

            {/* The iframe is mounted the FIRST time the user taps play.
                Once mounted it stays for the rest of the session so
                subsequent play/pause stays instant. */}
            {state !== "poster" && (
              <iframe
                ref={setIframeEl}
                key={nativeMode ? "native" : "custom"}
                src={nativeMode ? HERO_TUTORIAL_IFRAME_NATIVE : HERO_TUTORIAL_IFRAME}
                title="Calofay app tutorial"
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full border-0"
                data-testid="hero-tutorial-iframe"
              />
            )}
            <VolumeHint playing={state === "playing"} currentTime={currentTime} en={lang === "en"} />

            {/* Spinner shown while iframe boots + video buffers, before
                Stream fires the real `play` event. Prevents the user
                from seeing a black box between click and playback. */}
            {!nativeMode && state === "loading" && (
              <div
                className="absolute inset-0 grid place-items-center z-20 pointer-events-none"
                data-testid="hero-video-spinner"
              >
                <div className="w-14 h-14 rounded-full border-[3px] border-white/25 border-t-white animate-spin" />
              </div>
            )}

            {/* Transparent tap-target while playing so users can pause
                by tapping the video. Only covers the TOP portion so
                the custom scrubber below stays reachable — otherwise
                every tap on the seek bar would just toggle pause. */}
            {!nativeMode && state === "playing" && (
              <button
                type="button"
                onClick={togglePause}
                aria-label={t("video.aria_pause")}
                data-testid="hero-pause-overlay"
                className="absolute inset-x-0 top-0 bottom-12 z-20 bg-transparent focus:outline-none"
              />
            )}

            {/* Custom scrubber (seek bar) — Cloudflare Stream's built-in
                controls stay disabled because they render a second
                center play button. This slim bottom bar gives users
                click-to-seek + a visible playhead + total duration
                without bringing back the doubled play button. */}
            {!nativeMode && (state === "playing" || state === "paused") && duration > 0 && (
              <div
                className="absolute inset-x-0 bottom-0 z-30 pointer-events-none"
                data-testid="hero-video-controls"
              >
                <div className="pointer-events-auto px-3 pb-3 pt-6 bg-gradient-to-t from-black/70 via-black/40 to-transparent">
                  <div className="flex items-center gap-2 text-white text-[11px] font-semibold tabular-nums" dir="ltr">
                    <span data-testid="hero-video-current">{formatVideoTime(currentTime)}</span>
                    <input
                      type="range"
                      min={0}
                      max={duration}
                      step={0.1}
                      value={Math.min(currentTime, duration)}
                      // `dir=ltr` forces the native range track to
                      // map left→right regardless of the page's RTL
                      // direction. Without it clicking near the start
                      // of the track jumps to the END of the video
                      // (Chrome mirrors the value axis under `dir=rtl`).
                      dir="ltr"
                      onMouseDown={() => { isSeekingRef.current = true; }}
                      onTouchStart={() => { isSeekingRef.current = true; }}
                      onChange={(e) => {
                        const t = parseFloat(e.target.value);
                        setCurrentTime(t);
                        if (player) { try { player.currentTime = t; } catch {} }
                      }}
                      onMouseUp={() => { isSeekingRef.current = false; }}
                      onTouchEnd={() => { isSeekingRef.current = false; }}
                      className="flex-1 h-1.5 accent-brand-500 cursor-pointer"
                      aria-label={t("video.aria_seek")}
                      data-testid="hero-video-seek"
                    />
                    <span data-testid="hero-video-duration">{formatVideoTime(duration)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Orange play button — visible in `poster` and `paused`.
                Hidden while `loading` (spinner is up) and `playing`
                (pause overlay is up). */}
            {(state === "poster" || (!nativeMode && state === "paused")) && (
              <button
                type="button"
                onClick={state === "poster" ? start : togglePause}
                aria-label={t("video.aria_play")}
                data-testid="hero-play-btn"
                className="absolute inset-0 grid place-items-center focus:outline-none group z-20"
              >
                <span className="absolute w-24 h-24 rounded-full bg-brand-500/40 animate-ping" />
                <span className="absolute w-20 h-20 rounded-full bg-brand-500/25" />
                <span
                  className="relative w-16 h-16 rounded-full bg-gradient-to-br from-brand-400 via-brand-500 to-brand-600 grid place-items-center shadow-[0_8px_28px_-4px_rgba(249,115,22,0.65)] group-hover:scale-110 group-active:scale-95 transition-transform"
                  style={{ border: "3px solid rgba(255,255,255,0.95)" }}
                >
                  <Play className="w-7 h-7 text-white fill-white translate-x-[2px]" strokeWidth={0} />
                </span>
              </button>
            )}

            {/* Visible retry hint after a failed load — otherwise the
                bounce-back to poster is silent and a broken video is
                indistinguishable from a broken button. */}
            {hasError && state === "poster" && (
              <div
                className="absolute bottom-4 left-1/2 -translate-x-1/2 pointer-events-none z-30 max-w-[240px]"
                data-testid="hero-video-error"
              >
                <span className="inline-flex items-center gap-1.5 bg-red-600/90 backdrop-blur-sm text-white text-[11px] font-bold px-3 py-1.5 rounded-full border border-white/20 text-center leading-snug">
                  {t("video.error")}
                </span>
              </div>
            )}

            {/* Bottom chip only on the poster (before first tap) and only
                when there's no error hint being shown in its place. */}
            {state === "poster" && !hasError && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 pointer-events-none z-20">
                <span className="inline-flex items-center gap-1.5 bg-black/70 backdrop-blur-sm text-white text-[11px] font-bold px-3 py-1.5 rounded-full border border-white/20">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
                  {t("video.watch_now")}
                </span>
              </div>
            )}

            {/* Dynamic Island / notch — intentionally omitted.
                The poster image already renders the app's own header
                (fire streak, logo). Overlaying a black island on top
                clips the logo and adds no product value. */}
          </div>
        </div>

        {/* Floating chips — positioned OUTSIDE the phone so they don't
            fight the rounded corners. `z-40` ensures they float above
            the phone body's shadow. */}
        <div className="absolute -top-3 -right-3 hidden md:flex items-center gap-1.5 bg-white rounded-full shadow-floating px-3 py-2 border-2 border-neutral-900 rotate-[-6deg] z-40" data-testid="hero-chip-ai">
          <Bot className="w-3.5 h-3.5 text-brand-500" />
          <span className="text-[11px] font-bold text-neutral-900 whitespace-nowrap">{t("chip.ai_ar")}</span>
        </div>
        <div className="absolute -bottom-3 -left-3 hidden md:flex items-center gap-1.5 bg-neutral-900 rounded-full shadow-floating px-3 py-2 border-2 border-white rotate-[5deg] z-40" data-testid="hero-chip-arabic">
          <Sparkles className="w-3.5 h-3.5 text-brand-400" />
          <span className="text-[11px] font-bold text-white whitespace-nowrap">{t("chip.all_ar")}</span>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// 2 · PROBLEM / SOLUTION
// ═════════════════════════════════════════════════════════════════════

// Pairs of what a Calofay user knows every day. `ar` + `en` variants
// are held together so `ProblemSolution` can pick the current lang.
const PS_KNOW = [
  { ar: "كم سعرة يحتاج جسمك يوميًا",                    en: "How many calories your body needs each day" },
  { ar: "كم سعرة أكلت اليوم",                            en: "How many calories you've eaten today" },
  { ar: "كم سعرة حرقت من التمارين",                      en: "How many calories you've burned from workouts" },
  { ar: "كم باقي لك من السعرات",                         en: "How many calories you have left" },
  { ar: "احتياجك من البروتين والكارب والدهون",            en: "Your daily protein, carbs, and fat targets" },
  { ar: "كم أخذت وكم باقي لك من كل ماكرو",              en: "How much of each macro you've hit and what's left" },
  { ar: "كمية الماء اللي شربتها وكم باقي لهدفك",         en: "How much water you've had and what's left to your goal" },
  { ar: "عدد خطواتك وحركتك اليومية",                    en: "Your daily step count and movement" },
  { ar: "تمارينك والسعرات المحروقة منها",               en: "Your workouts and the calories they burned" },
  { ar: "وزنك وكيف يتغير مع الوقت",                     en: "Your weight and how it changes over time" },
  { ar: "هل أنت فعلًا قريب من هدفك أو لا",              en: "Whether you're actually close to your goal — or not" },
  { ar: "كيف كان التزامك خلال الأسبوع",                 en: "How consistent you've been this week" },
  { ar: "اتجاه تقدمك بدل ما تعتمد على وزن يوم واحد",     en: "Your trend line — not a single misleading weigh-in" },
  { ar: "صور تقدمك قبل وبعد",                           en: "Your before/after progress photos" },
  { ar: "هل أكلك اليوم متوازن مع هدفك",                 en: "Whether today's meals fit your goal" },
  { ar: "وش تقدر تأكل بالسعرات والماكروز المتبقية لك",  en: "What to eat with the calories and macros you've got left" },
  { ar: "متى تقريبًا ممكن توصل لهدفك حسب تقدمك",         en: "A realistic ETA to reach your goal at your current pace" },
];

function ProblemSolution({ onStart }) {
  const { t, lang, isRTL } = useLandingLang();
  return (
    <DarkSection testid="lv2-problem">
      <div className="max-w-5xl mx-auto">
        <SectionBubble
          dark
          eyebrow={t("ps.eyebrow")}
          title={t("ps.title")}
          sub={t("ps.sub")}
          testid="lv2-problem-header"
        />

        {/* Solution reveal: "مع كالوفاي تعرف:" */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border-2 border-brand-500/60 text-brand-400 text-xs font-black px-4 py-1.5 rounded-full tracking-wide">
            {t("ps.solution_badge")}
          </div>
        </div>

        {/* 17-item grid with orange numbered rings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 max-w-4xl mx-auto mb-10">
          {PS_KNOW.map((item, i) => (
            <motion.div
              key={item.ar}
              initial={{ opacity: 0, x: 12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.35, delay: (i % 8) * 0.04, ease: "easeOut" }}
              className="flex items-start gap-3 py-2.5 px-4 rounded-xl bg-white/[0.04] border border-white/10 hover:border-brand-500/50 hover:bg-white/[0.06] transition-colors"
              dir={isRTL ? "rtl" : "ltr"}
            >
              <div className="shrink-0 mt-0.5">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black orange-gradient text-white">
                  {String(i + 1).padStart(2, "0")}
                </div>
              </div>
              <span className="text-[15px] md:text-base text-white/90 leading-relaxed font-medium">
                {lang === "en" ? item.en : item.ar}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Bold closing statement */}
        <div className="max-w-3xl mx-auto text-center">
          <div
            className="relative rounded-3xl px-6 md:px-10 py-8 md:py-10 border-2"
            style={{
              background:
                "linear-gradient(135deg, rgba(249,115,22,0.10) 0%, rgba(249,115,22,0.04) 100%)",
              borderColor: "rgba(249,115,22,0.45)",
            }}
          >
            <div className="absolute -top-3 right-1/2 translate-x-1/2 text-3xl opacity-20 select-none">
              ”
            </div>
            <p className="text-lg sm:text-xl md:text-2xl font-black leading-relaxed text-white">
              {t("ps.close_1")}
              <br />
              <span
                style={{
                  backgroundImage:
                    "linear-gradient(180deg, #ffb066 0%, #f97316 55%, #c2410c 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {t("ps.close_2")}
              </span>
            </p>
            <div className="mt-6">
              <PillCta onClick={onStart} testid="lv2-problem-cta">{t("ps.cta")}</PillCta>
            </div>
          </div>
        </div>
      </div>
    </DarkSection>
  );
}

// ═════════════════════════════════════════════════════════════════════
// 3 · FEATURES & BENEFITS — multiple sub-blocks
// ═════════════════════════════════════════════════════════════════════

function FeaturesHub({ onStart }) {
  const { t, lang } = useLandingLang();
  return (
    <LightSection testid="lv2-features">
      <div className="max-w-5xl mx-auto">
        <SectionBubble
          eyebrow={t("feat.eyebrow")}
          title={t("feat.title")}
          sub={t("feat.sub")}
          testid="lv2-features-header"
        />
        <div className="space-y-5">
          {FEATURES.map(({ n, title, title_en, desc, desc_en, Icon, color, Mock }) => (
            <div key={n} className="rounded-3xl bg-white border-2 border-neutral-900 p-6 md:p-8 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center">
                <div>
                  <FeatureNumber n={n} />
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl border-2 ${COLOR_MAP[color]} shrink-0`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-black text-neutral-900 leading-tight">{lang === "en" ? title_en : title}</h3>
                  </div>
                  <p className="text-sm md:text-base text-neutral-600 leading-relaxed max-w-lg">{lang === "en" ? desc_en : desc}</p>
                </div>
                <div className="w-full md:w-64 shrink-0">
                  <Mock />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </LightSection>
  );
}

// ═════════════════════════════════════════════════════════════════════
// 3.5 · WHY CALOFAY IS DIFFERENT — dedicated section (light theme so it
// alternates with the dark Competitors section that follows below).
// ═════════════════════════════════════════════════════════════════════

function DifferentSection({ onStart }) {
  const { t, lang } = useLandingLang();
  return (
    <LightSection testid="lv2-different">
      <div className="max-w-5xl mx-auto">
        <SectionBubble
          eyebrow={t("diff.eyebrow")}
          title={t("diff.title")}
          sub={t("diff.sub")}
          testid="lv2-different-header"
        />
        <div className="rounded-3xl bg-white border-2 border-neutral-900 overflow-hidden shadow-sm mb-8">
          {/* Header row */}
          <div className="grid grid-cols-[2fr_1fr_1fr] text-xs md:text-sm font-black border-b-2 border-neutral-900">
            <div className="px-4 md:px-6 py-4 text-neutral-500">{t("diff.col_feature")}</div>
            <div className="px-3 py-4 text-center bg-neutral-50 text-neutral-500 border-x-2 border-neutral-900">
              {t("diff.col_traditional")}
            </div>
            <div className="relative px-3 py-4 text-center orange-gradient text-white">
              Calofay
            </div>
          </div>
          {/* Rows */}
          {COMPARE.map((row, i) => (
            <div key={row.us}
              className={`grid grid-cols-[2fr_1fr_1fr] text-sm items-center border-b border-neutral-100 last:border-b-0
                          ${i % 2 === 0 ? "bg-white" : "bg-neutral-50/60"}`}>
              <div className="px-4 md:px-6 py-3.5 text-neutral-800 font-medium leading-tight">
                {lang === "en" ? row.us_en : row.us}
              </div>
              <div className="px-3 py-3.5 flex items-center justify-center border-x border-neutral-100">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-rose-50 text-rose-500 border-2 border-rose-500">
                  <X className="w-4 h-4" strokeWidth={3} />
                </span>
              </div>
              <div className="px-3 py-3.5 flex items-center justify-center bg-brand-500/[0.06]">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-50 text-emerald-600 border-2 border-emerald-500">
                  <Check className="w-4 h-4" strokeWidth={3} />
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center">
          <p className="text-base sm:text-lg font-bold text-neutral-900 mb-1">
            {t("diff.close_1")}
          </p>
          <p className="text-sm text-neutral-600 mb-6">
            {t("diff.close_2")}
          </p>
          <PillCta onClick={onStart} testid="lv2-different-cta">{t("diff.cta")}</PillCta>
        </div>
      </div>
    </LightSection>
  );
}

/** Big brand-orange "الميزة X" badge that anchors each sub-feature in
 *  the Features section, so visitors can *see* they're progressing
 *  through a numbered checklist. */
function FeatureNumber({ n }) {
  const { lang, t } = useLandingLang();
  // Arabic ordinals feel warmer than the generic "Feature 01" but
  // don't translate 1:1 to English — the English variant just reads
  // "Feature 01" so it stays professional & scannable.
  const ordinalsAr = ["", "الأولى", "الثانية", "الثالثة", "الرابعة", "الخامسة",
                      "السادسة", "السابعة", "الثامنة", "التاسعة", "العاشرة",
                      "الحادية عشرة", "الثانية عشرة", "الثالثة عشرة", "الرابعة عشرة",
                      "الخامسة عشرة"];
  const sublabel = lang === "en" ? `#${String(n).padStart(2, "0")}` : ordinalsAr[n];
  const label = t("feat.number_label");
  return (
    <div className="inline-flex items-center gap-3 mb-4">
      <div className="orange-gradient text-white w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center shadow-floating font-black text-lg md:text-xl border-2 border-neutral-900">
        {String(n).padStart(2, "0")}
      </div>
      <div className="text-right">
        <div className="text-[10px] text-neutral-500 font-bold uppercase tracking-wide">{label}</div>
        <div className="text-sm md:text-base font-black text-brand-600">{sublabel}</div>
      </div>
    </div>
  );
}

// 3.1 — Logging methods
const LOG_METHODS = [
  { Icon: Camera,  title: "صور وجبتك",           color: "brand",
    desc: "صور أكلك وخلي الذكاء الاصطناعي يقترح المكونات والكميات والسعرات والماكروز، وبعدها راجع وعدل قبل الحفظ." },
  { Icon: Mic,     title: "سجل بالصوت",          color: "emerald",
    desc: "قل وش أكلت بصوتك، وكالوفاي يحول كلامك إلى وجبة مسجلة.",
    example: "\"أكلت صحن كبسة دجاج مع لبن\"" },
  { Icon: Barcode, title: "امسح الباركود أو ملصق التغذية", color: "sky",
    desc: "امسح باركود المنتج أو صور جدول القيم الغذائية وخلي كالوفاي يساعدك تسجله بسرعة." },
  { Icon: Star,    title: "المفضلة",             color: "amber",
    desc: "احفظ وجباتك المتكررة وارجع لها بلمسة بدل ما تسجلها من جديد كل مرة." },
  { Icon: Edit3,   title: "أضفها بنفسك",         color: "violet",
    desc: "ولو تحب تتحكم بكل شيء، تقدر تضيف أكلك والسعرات والماكروز يدويًا." },
];

const COLOR_MAP = {
  brand:   "bg-brand-50 text-brand-600 border-brand-100",
  emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
  sky:     "bg-sky-50 text-sky-600 border-sky-100",
  amber:   "bg-amber-50 text-amber-600 border-amber-100",
  violet:  "bg-violet-50 text-violet-600 border-violet-100",
  rose:    "bg-rose-50 text-rose-600 border-rose-100",
};

// ═════════════════════════════════════════════════════════════════════
// 3 · MASTER FEATURES LIST — one flat sequence 01 → 12. Each feature
// renders as a bordered rectangle: numbered badge + icon + title +
// description + a small live-looking mockup on the side. No group
// headers within the section, so the numbering never resets.
// ═════════════════════════════════════════════════════════════════════

function LoggingMethodsMock() {
  const { lang } = useLandingLang();
  const items = lang === "en" ? [
    { Icon: Camera,  l: "Photo" },
    { Icon: Mic,     l: "Voice" },
    { Icon: Barcode, l: "Barcode" },
    { Icon: Star,    l: "Favorites" },
    { Icon: Edit3,   l: "Manual" },
  ] : [
    { Icon: Camera,  l: "صور" },
    { Icon: Mic,     l: "تكلم" },
    { Icon: Barcode, l: "باركود" },
    { Icon: Star,    l: "المفضلة" },
    { Icon: Edit3,   l: "يدوي" },
  ];
  return (
    <div className="rounded-xl bg-white border-2 border-brand-500 p-3 grid grid-cols-5 gap-1.5">
      {items.map(({ Icon, l }) => (
        <div key={l} className="flex flex-col items-center justify-center py-2 rounded-lg bg-brand-50 text-brand-600">
          <Icon className="w-4 h-4 mb-1" />
          <span className="text-[9px] font-black">{l}</span>
        </div>
      ))}
    </div>
  );
}

function RecipesMock() {
  const { lang } = useLandingLang();
  const rows = lang === "en" ? [
    ["Chicken Kabsa",    "45 min · 520 kcal"],
    ["Quinoa salad",     "10 min · 280 kcal"],
    ["Protein smoothie", "3 min · 210 kcal"],
  ] : [
    ["كبسة دجاج",     "45 دقيقة · 520 kcal"],
    ["سلطة كينوا",   "10 دقائق · 280 kcal"],
    ["سموذي بروتين",  "3 دقائق · 210 kcal"],
  ];
  const foot = lang === "en" ? "+ 497 more recipes" : "+ 497 وصفة أخرى";
  return (
    <div className="rounded-xl bg-white border-2 border-emerald-500 p-3 space-y-2">
      {rows.map(([t, m]) => (
        <div key={t} className="flex items-center gap-2 text-[11px]">
          <ChefHat className="w-3 h-3 text-emerald-600 shrink-0" />
          <span className="text-neutral-800 font-bold flex-1">{t}</span>
          <span className="text-neutral-500 text-[10px]">{m}</span>
        </div>
      ))}
      <div className="text-[9px] text-emerald-700 text-center pt-1 border-t border-emerald-100 font-bold">
        {foot}
      </div>
    </div>
  );
}

function ShoppingMock() {
  const { lang } = useLandingLang();
  const title = lang === "en" ? "This week's shopping list" : "قائمة تسوق الأسبوع";
  const rows = lang === "en"
    ? [["Chicken", true], ["Rice", true], ["Tomato", false], ["Cucumber", false], ["Yogurt", true], ["Eggs", false]]
    : [["دجاج", true], ["أرز", true], ["طماطم", false], ["خيار", false], ["زبادي", true], ["بيض", false]];
  return (
    <div className="rounded-xl bg-white border-2 border-amber-500 p-3">
      <div className="text-[10px] text-amber-700 font-black mb-2">{title}</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
        {rows.map(([n, done]) => (
          <div key={n} className="flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 rounded-sm border-2 ${done ? "bg-amber-500 border-amber-500" : "border-neutral-300"}`}>
              {done && <Check className="w-2.5 h-2.5 text-white" strokeWidth={4} />}
            </span>
            <span className={done ? "line-through text-neutral-400" : "text-neutral-800"}>{n}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProgressMiniMock() {
  const { lang } = useLandingLang();
  const before = lang === "en" ? "Before" : "قبل";
  const after  = lang === "en" ? "After"  : "بعد";
  return (
    <div className="rounded-xl overflow-hidden border-2 border-violet-500 aspect-[16/9] relative bg-neutral-100">
      {/* In RTL parents, the FIRST grid child renders on the RIGHT.
          Order below: BEFORE first → renders right. AFTER second → renders left. */}
      <div className="absolute inset-0 grid grid-cols-2">
        {/* BEFORE — right (heavier) */}
        <div className="relative overflow-hidden">
          <img src="/before-after-before.webp" alt={before} draggable={false} loading="lazy"
               className="w-full h-full object-cover" style={{ objectPosition: "50% center" }} />
          <div className="absolute top-1 right-1 bg-rose-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full">{before}</div>
        </div>
        {/* AFTER — left (leaner) */}
        <div className="relative overflow-hidden">
          <img src="/before-after-after.webp" alt={after} draggable={false} loading="lazy"
               className="w-full h-full object-cover" style={{ objectPosition: "78% center" }} />
          <div className="absolute top-1 left-1 bg-emerald-600 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full">{after}</div>
        </div>
      </div>
      <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[2px] bg-white" />
    </div>
  );
}

function CoachShareMock() {
  const { lang } = useLandingLang();
  const initial = lang === "en" ? "C" : "م";
  const opened  = lang === "en" ? "Your coach opened the link" : "مدربي فتح الرابط";
  const ago     = lang === "en" ? "3 min ago" : "قبل 3 دقائق";
  return (
    <div className="rounded-xl bg-neutral-900 border-2 border-violet-500 p-3 text-white">
      <div className="flex items-center gap-2 text-[10px] mb-2">
        <Lock className="w-3 h-3 text-emerald-400 shrink-0" />
        <span className="text-neutral-400 truncate">calofay.app/share/x8k4…</span>
      </div>
      <div className="flex items-center gap-2 bg-white/[0.06] rounded-lg px-2 py-1.5 border border-white/10">
        <div className="w-6 h-6 rounded-full bg-brand-500 text-white text-[10px] font-black flex items-center justify-center shrink-0">{initial}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] text-white font-bold leading-tight">{opened}</div>
          <div className="text-[9px] text-white/50 leading-tight">{ago}</div>
        </div>
        <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" strokeWidth={3} />
      </div>
    </div>
  );
}

function VideoImportMock() {
  const { lang } = useLandingLang();
  const isEn = lang === "en";
  const title = isEn ? "Import a recipe from video" : "استيراد وصفة من فيديو";
  const steps = isEn ? [
    ["Open TikTok / Instagram", "1"],
    ["Tap the share button",    "2"],
    ["Choose Copy Link",         "3"],
    ["Paste into Calofay · extract", "4"],
  ] : [
    ["افتح TikTok / Instagram", "١"],
    ["اضغط زر المشاركة",       "٢"],
    ["اختر Copy Link",           "٣"],
    ["الصق في كالوفاي · استخراج", "٤"],
  ];
  const foot = isEn ? "Works with TikTok · Instagram Reels" : "يعمل مع TikTok · Instagram Reels";
  return (
    <div className="rounded-xl bg-neutral-900 border-2 border-purple-500 p-3 text-white">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-3.5 h-3.5 text-purple-400" />
        <span className="text-[10px] font-black text-white">{title}</span>
      </div>
      <div className="space-y-1.5">
        {steps.map(([t, n]) => (
          <div key={t} className="flex items-center gap-2 text-[10px]">
            <span className="w-4 h-4 rounded-full bg-purple-500 text-white flex items-center justify-center font-black text-[9px] shrink-0">{n}</span>
            <span className="text-white/85 flex-1">{t}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 bg-white/[0.08] border border-purple-500/40 rounded-lg px-2 py-1 text-[9px] text-purple-200 text-center font-bold">
        {foot}
      </div>
    </div>
  );
}

function AICoachMock() {
  const { lang } = useLandingLang();
  const isEn = lang === "en";
  return (
    <div className="rounded-xl bg-white border-2 border-violet-500 p-3 space-y-2">
      <div className="flex items-start gap-2">
        <div className="w-6 h-6 rounded-full bg-violet-500 text-white flex items-center justify-center shrink-0">
          <Bot className="w-3 h-3" />
        </div>
        <div className="flex-1 bg-violet-50 border border-violet-100 rounded-lg rounded-tl-none px-2 py-1.5 text-[10px] text-neutral-800">
          {isEn
            ? <>You have <b>400 kcal</b> left — try a tuna salad on whole-wheat bread.</>
            : <>باقي لك <b>400 سعرة</b> — جرّب صحن سلطة تونة مع خبز أسمر.</>}
        </div>
      </div>
      <div className="flex items-start gap-2 flex-row-reverse">
        <div className="w-6 h-6 rounded-full bg-neutral-200 flex items-center justify-center shrink-0 text-[10px] font-black text-neutral-700">{isEn ? "Y" : "أ"}</div>
        <div className="bg-neutral-100 border border-neutral-200 rounded-lg rounded-tr-none px-2 py-1.5 text-[10px] text-neutral-800">
          {isEn ? "How much protein is in that?" : "كم بروتين فيه؟"}
        </div>
      </div>
    </div>
  );
}

function WhatsAppMock() {
  const { lang } = useLandingLang();
  return (
    <div className="rounded-xl bg-emerald-50 border-2 border-emerald-500 p-3">
      <div className="flex items-center gap-2 mb-2">
        <svg viewBox="0 0 24 24" className="w-4 h-4 fill-emerald-600" aria-hidden>
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zM20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.892c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652a11.882 11.882 0 005.71 1.447h.006c6.585 0 11.946-5.336 11.949-11.896 0-3.176-1.24-6.165-3.495-8.407z"/>
        </svg>
        <span className="text-[10px] font-black text-emerald-800">{lang === "en" ? "Support team" : "أخصائية التغذية"}</span>
        <span className="ml-auto text-[9px] text-emerald-700">{lang === "en" ? "Online" : "متاح"}</span>
      </div>
      <div className="bg-white rounded-lg px-2 py-1.5 text-[10px] text-neutral-800 border border-emerald-100">
        {lang === "en" ? "How do I log a meal from a photo? 📷" : "هل هالوجبة تناسب هدفي؟ 📷"}
      </div>
    </div>
  );
}

function JournalMock() {
  const { lang } = useLandingLang();
  const isEn = lang === "en";
  return (
    <div className="rounded-xl bg-neutral-950 border-2 border-amber-500/40 p-3 shadow-inner">
      {/* Header */}
      <div className="flex items-center gap-1.5 mb-2.5">
        <NotebookPen className="w-3.5 h-3.5 text-brand-400" strokeWidth={2.5} />
        <span className="text-[10px] font-black text-white">{isEn ? "Daily journal" : "دفتر اليوم"}</span>
        <ChevronDown className="w-3 h-3 text-white/50 rotate-180" />
        <span className="ml-auto flex items-center gap-1 text-[9px] text-white/60">
          <Share2 className="w-2.5 h-2.5" /> {isEn ? "Share" : "مشاركة"}
        </span>
      </div>

      {/* Journal card */}
      <div className="rounded-lg bg-gradient-to-b from-brand-500/[0.15] to-brand-500/[0.05] border border-brand-500/25 p-2 mb-2">
        <div className={`text-[10px] font-black text-white mb-1.5 ${isEn ? "text-left" : "text-right"}`}>{isEn ? "My journal" : "يومياتي"}</div>
        <div className={`rounded-md bg-black/40 border border-white/10 px-2 py-1.5 text-[9px] text-white/50 leading-relaxed ${isEn ? "text-left" : "text-right"}`}>
          {isEn
            ? "How was your day? How are you feeling? Notes on hunger, energy, mood…"
            : "أيش صار في يومك؟ كيف حالتك النفسية؟ ملاحظاتك عن جوعك، طاقتك، مزاجك…"}
        </div>
      </div>

      {/* Tasks card */}
      <div className="rounded-lg bg-gradient-to-b from-brand-500/[0.15] to-brand-500/[0.05] border border-brand-500/25 p-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[9px] font-black text-brand-400">{isEn ? "+ Add" : "+ إضافة"}</span>
          <span className="text-[10px] font-black text-white">{isEn ? "Today's to-dos" : "قائمة مهام اليوم"}</span>
        </div>
        <div className={`text-[9px] text-white/50 ${isEn ? "text-left" : "text-right"}`}>
          {isEn ? "No tasks yet — add one to keep track of your day" : "لا توجد مهام — أضف واحدة لتتبع يومك"}
        </div>
      </div>
    </div>
  );
}

const FEATURES = [
  { n: 1,  title: "سجّل أكلك بـ 5 طرق ذكية",   title_en: "Log food 5 smart ways",
    Icon: Camera,   color: "brand",
    desc: "صور وجبتك · تكلم عن وش أكلت · امسح الباركود · اختر من مفضلتك · أو اكتب يدوي. كالوفاي يحدد المكونات والسعرات والماكروز نيابة عنك.",
    desc_en: "Photo · voice · barcode · favorites · manual entry. Calofay figures out the ingredients, calories, and macros for you.",
    Mock: LoggingMethodsMock },
  { n: 2,  title: "+500 وصفة عربية جاهزة", title_en: "500+ ready-to-cook recipes",
    Icon: ChefHat, color: "emerald",
    desc: "مكتبة وصفات عربية كاملة مع المكونات وطريقة التحضير والسعرات والماكروز — من الفطور إلى العشاء، وحلى ومشروبات.",
    desc_en: "A full recipe library — ingredients, step-by-step instructions, and macros — for breakfast, lunch, dinner, snacks, and drinks.",
    Mock: RecipesMock },
  { n: 3,  title: "استيراد وصفة من فيديو TikTok أو Instagram",
    title_en: "Import recipes from TikTok or Instagram",
    Icon: Import, color: "violet",
    desc: "شفت وصفة تعجبك في ريلز أو تيك توك؟ الصق رابط الفيديو داخل كالوفاي واستخرج المكونات والخطوات والسعرات تلقائيًا — بضغطة زر.",
    desc_en: "Saw a recipe on Reels or TikTok? Paste the link — Calofay pulls the ingredients, steps, and calories for you in one tap.",
    Mock: VideoImportMock },
  { n: 4,  title: "قائمة تسوق ذكية تلقائية",  title_en: "Auto-built smart shopping list",
    Icon: ShoppingCart, color: "amber",
    desc: "أضف وصفاتك وخلي كالوفاي يجمع كل المكونات في قائمة تسوق مرتبة حسب الأقسام (خضار · لحوم · ألبان)، وشطّب الأشياء اللي اشتريتها.",
    desc_en: "Pick your recipes and Calofay bundles the ingredients into a shopping list sorted by aisle (produce · meat · dairy). Check items off as you grab them.",
    Mock: ShoppingMock },
  { n: 5,  title: "السعرات",   title_en: "Calories",
    Icon: Flame,      color: "brand",
    desc: "اعرف كم سعرة تحتاج، كم أكلت وكم باقي لك في اليوم — بعرض واضح ودائم.",
    desc_en: "Know exactly how many calories you need, ate, and still have left — always visible, always clear.",
    Mock: () => <TrackerMock kind="calories" /> },
  { n: 6,  title: "الماكروز",  title_en: "Macros",
    Icon: BarChart3,  color: "emerald",
    desc: "تابع البروتين والكارب والدهون كل يوم بطريقة مرئية وسهلة.",
    desc_en: "Track protein, carbs, and fat every day with clean visual rings.",
    Mock: () => <TrackerMock kind="macros" /> },
  { n: 7,  title: "الماء",     title_en: "Water",
    Icon: Droplets,   color: "sky",
    desc: "حدد هدفك اليومي من الماء (كوب / لتر) واضغط زر واحد لكل كوب تشربه — تذكيرات ذكية طول اليوم.",
    desc_en: "Set your daily water goal (cups or liters), tap once per glass, and get smart reminders throughout the day.",
    Mock: () => <TrackerMock kind="water" /> },
  { n: 8,  title: "التمارين",   title_en: "Workouts",
    Icon: Dumbbell,   color: "violet",
    desc: "سجّل تمرينك (وقت + شدة + نوع) واحسب السعرات المحروقة تلقائيًا — أكثر من 100 نوع تمرين.",
    desc_en: "Log your workout (time · intensity · type) and get calories burned auto-calculated — 100+ exercise types.",
    Mock: () => <TrackerMock kind="exercise" /> },
  { n: 9,  title: "الخطوات",    title_en: "Steps",
    Icon: Footprints, color: "amber",
    desc: "تابع خطواتك اليومية وتقدر تربطها بتطبيق الصحة عالآيفون علشان تجيب الخطوات تلقائي.",
    desc_en: "Track daily steps and connect Apple Health so they sync in automatically.",
    Mock: () => <TrackerMock kind="steps" /> },
  { n: 10, title: "الوزن",     title_en: "Weight",
    Icon: Scale,      color: "rose",
    desc: "سجّل وزنك بكم لمسة واشوف تغيّره بمخطط اتجاهي أسبوعي وشهري — بدون تخمين.",
    desc_en: "Log your weight in a couple of taps and watch a real trend line unfold across weeks and months — no more guessing.",
    Mock: () => <TrackerMock kind="weight" /> },
  { n: 11, title: "صور قبل / بعد",  title_en: "Before/after photos",
    Icon: Camera,  color: "violet",
    desc: "احفظ صور تقدمك أول بأول وقارن قبل / بعد جنب بعض في نفس التطبيق — تشوف التغيير حتى لو الميزان ما يوضحه.",
    desc_en: "Save progress photos over time and slide before/after side-by-side inside the app — see changes the scale can miss.",
    Mock: ProgressMiniMock },
  { n: 12, title: "شارك تقدمك مع مدربك",  title_en: "Share progress with your coach",
    Icon: Share2,  color: "violet",
    desc: "أعطِ مدربك أو دكتور التغذية رابط خاص محمي بكلمة سر — يفتحه من جواله ويشوف وزنك وسعراتك وصور تقدمك.",
    desc_en: "Send your coach or dietitian a password-protected link — they open it on their phone and see your weight, calories, and progress photos.",
    Mock: CoachShareMock },
  { n: 13, title: "مدرب كالوفاي بالذكاء الاصطناعي",  title_en: "Your personal AI coach",
    Icon: Bot, color: "violet",
    desc: "دردشة مباشرة (مثل ChatGPT) بس مدرّبة على هدفك وسعراتك وماكروزك — تسألها أي سؤال عن التغذية، تقترح لك وجباتك اليومية، وتعطيك بدائل تناسب اللي متبقّي لك.",
    desc_en: "A ChatGPT-style chat that already knows your goal, calories, and macros. Ask any nutrition question, get daily meal ideas, and swap in options that fit your remaining budget.",
    Mock: AICoachMock },
  { n: 14, title: "أخصائية تغذية عبر واتساب",  title_en: "WhatsApp support",
    Icon: MessageCircle, color: "emerald",
    desc: "لما تحتاج دعم بشري أو استشارة، أخصائية التغذية تجاوبك مباشرة عبر واتساب عن أكلك، هدفك، أو أي شيء محتار فيه.",
    desc_en: "Have a question? Our support team answers directly on WhatsApp — about using the app, your account, or anything you're stuck on.",
    Mock: WhatsAppMock },
  { n: 15, title: "دفتر اليوم",  title_en: "Daily journal",
    Icon: NotebookPen, color: "amber",
    desc: "دوّن يومياتك، جوعك، طاقتك، ومزاجك — واكتب قائمة مهام اليوم عشان تتبع التزامك خطوة خطوة. يعطيك صورة واضحة عن سلوكك اليومي، لأن الرحلة مو بس أرقام.",
    desc_en: "Journal your day — hunger, energy, mood — and jot a to-do list to track consistency. Because the journey isn't only numbers.",
    Mock: JournalMock },
];

function FeaturesLogging()  { return null; /* superseded by FEATURES flat list */ }
function FeaturesRecipes()  { return null; }
function FeaturesTrackers() { return null; }
function FeaturesProgress() { return null; }
function FeaturesHelp()     { return null; }

function FeaturesLoggingOld() {
  return (
    <div className="mb-16">
      <div className="text-center mb-8">
        <FeatureNumber n={1} />
        <h3 className="text-xl sm:text-2xl font-black text-neutral-900 mb-2">
          سجل أكلك بالطريقة اللي تناسبك
        </h3>
        <p className="text-sm text-neutral-600 max-w-xl mx-auto">
          ما يحتاج تضيع وقتك تبحث عن كل مكون. اختر الطريقة الأسهل لك وسجل وجبتك خلال لحظات.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {LOG_METHODS.map(({ Icon, title, desc, example, color }) => (
          <div key={title} className="rounded-2xl bg-white border-2 border-neutral-900 p-5 hover:shadow-floating transition">
            <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl border ${COLOR_MAP[color]} mb-3`}>
              <Icon className="w-5 h-5" />
            </div>
            <h4 className="text-base font-bold text-neutral-900 mb-2">{title}</h4>
            <p className="text-sm text-neutral-600 leading-relaxed">{desc}</p>
            {example && (
              <div className="mt-3 text-xs text-neutral-700 bg-neutral-50 rounded-lg px-3 py-2 border-2 border-neutral-900">
                <span className="text-neutral-400 text-[10px] block mb-0.5">مثلاً:</span>
                {example}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// 3.2 — Recipes cluster
const RECIPE_BLOCKS = [
  { Icon: Bot,       title: "سو وصفة بالذكاء الاصطناعي",
    desc: "قل مثلاً: \"أبغى عشاء أقل من 500 سعرة وعالي بالبروتين\" وخلي كالوفاي يقترح لك وصفة تناسب طلبك." },
  { Icon: Import,    title: "احفظ وصفاتك الخاصة",
    desc: "عندك وصفة من رابط أو نص أو صورة؟ استوردها واحفظها وعدلها داخل كالوفاي." },
  { Icon: Heart,     title: "مفضلتك دائمًا قريبة",
    desc: "احفظ الوصفات اللي تعجبك وارجع لها بأي وقت." },
];

function FeaturesRecipesOld() {
  return (
    <div className="mb-16">
      <div className="text-center mb-8">
        <FeatureNumber n={2} />
        <h3 className="text-xl sm:text-2xl font-black text-neutral-900 mb-2">
          ما تعرف وش تأكل؟ عندك أكثر من <span className="text-brand-500">500 وصفة</span>
        </h3>
        <p className="text-sm text-neutral-600 max-w-xl mx-auto">
          افتح مكتبة كالوفاي واختر من أكثر من 500 وصفة عربية جاهزة مع المكونات، طريقة التحضير، السعرات والماكروز.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {RECIPE_BLOCKS.map(({ Icon, title, desc }) => (
          <div key={title} className="rounded-2xl bg-white border-2 border-neutral-900 p-5">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-brand-50 text-brand-600 border border-brand-100 mb-3">
              <Icon className="w-5 h-5" />
            </div>
            <h4 className="text-base font-bold text-neutral-900 mb-2">{title}</h4>
            <p className="text-sm text-neutral-600 leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
      <ShoppingListMock />
    </div>
  );
}

/** Visual: an auto-generated shopping list, organized by category. */
function ShoppingListMock() {
  const cats = [
    { name: "خضار",  items: ["طماطم", "بصل", "خيار", "خس"] },
    { name: "لحوم",  items: ["دجاج", "لحم مفروم"] },
    { name: "ألبان", items: ["حليب", "جبن قريش", "لبن"] },
    { name: "حبوب",  items: ["أرز", "خبز أسمر", "شوفان"] },
  ];
  return (
    <div className="rounded-3xl bg-gradient-to-br from-brand-50/60 to-white border-2 border-neutral-900 p-6 md:p-8">
      <div className="flex items-start gap-3 mb-5">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white text-brand-600 border border-brand-100 shrink-0">
          <ShoppingCart className="w-6 h-6" />
        </div>
        <div>
          <h4 className="text-lg font-black text-neutral-900 mb-1">
            وقائمة تسوقك؟ كالوفاي يجهزها لك
          </h4>
          <p className="text-sm text-neutral-600 leading-relaxed">
            أضف وصفاتك وخلي كالوفاي يجمع مكوناتها في قائمة تسوق واحدة مرتبة حسب الأقسام. وتقدر تشطب الأشياء اللي اشتريتها أول بأول.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cats.map((c, i) => (
          <div key={c.name} className="rounded-xl bg-white p-3 border-2 border-neutral-900">
            <div className="text-[11px] font-bold text-brand-600 mb-2">{c.name}</div>
            <ul className="space-y-1.5">
              {c.items.map((it, idx) => (
                <li key={it} className="flex items-center gap-1.5 text-xs text-neutral-700">
                  <span className={`inline-block w-3 h-3 rounded-sm border ${i === 0 && idx < 2 ? "bg-brand-500 border-brand-500" : "border-neutral-300"}`}>
                    {i === 0 && idx < 2 && <Check className="w-2.5 h-2.5 text-white" strokeWidth={4} />}
                  </span>
                  <span className={i === 0 && idx < 2 ? "line-through text-neutral-400" : ""}>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// 3.3 — Trackers ("more than a calorie counter")
// Each tracker is a substantial standalone feature block: bigger icon
// + prominent label + description + a small mockup that shows the
// feature in action, so visitors read every one as its own capability
// (Water, Exercise, Steps, Weight, etc.).
const TRACKERS = [
  { Icon: Flame,      title: "السعرات",  color: "brand",
    desc: "اعرف كم سعرة تحتاج، كم أكلت وكم باقي لك في اليوم — بعرض واضح ودائم.",
    mock: "calories" },
  { Icon: BarChart3,  title: "الماكروز", color: "emerald",
    desc: "تابع البروتين والكارب والدهون كل يوم بطريقة مرئية وسهلة.",
    mock: "macros" },
  { Icon: Droplets,   title: "الماء",    color: "sky",
    desc: "حدد هدفك اليومي من الماء (كوب / لتر) واضغط زر واحد لكل كوب تشربه — تذكيرات ذكية طول اليوم.",
    mock: "water" },
  { Icon: Dumbbell,   title: "التمارين",  color: "violet",
    desc: "سجّل تمرينك (وقت + شدة + نوع) واحسب السعرات المحروقة تلقائيًا — أكثر من 100 نوع تمرين.",
    mock: "exercise" },
  { Icon: Footprints, title: "الخطوات",   color: "amber",
    desc: "تابع خطواتك اليومية وتقدر تربطها بتطبيق الصحة عالآيفون علشان تجيب الخطوات تلقائي.",
    mock: "steps" },
  { Icon: Scale,      title: "الوزن",    color: "rose",
    desc: "سجّل وزنك بكم لمسة واشوف تغيّره بمخطط اتجاهي أسبوعي وشهري — بدون تخمين.",
    mock: "weight" },
];

function TrackerMock({ kind }) {
  const { lang } = useLandingLang();
  const isEn = lang === "en";
  if (kind === "calories") {
    return (
      <div className="rounded-xl bg-brand-50/60 border-2 border-brand-500 p-3">
        <div className="flex items-baseline gap-1.5 justify-center">
          <span className="text-2xl font-black text-brand-600">1,588</span>
          <span className="text-[10px] text-neutral-500">{isEn ? "of 2,143" : "من 2,143"}</span>
        </div>
        <div className="h-2 rounded-full bg-white mt-2 overflow-hidden">
          <div className="h-full orange-gradient" style={{ width: "74%" }} />
        </div>
        <div className="text-[10px] text-neutral-500 mt-1 text-center">{isEn ? "555 kcal left" : "متبقي 555 سعرة"}</div>
      </div>
    );
  }
  if (kind === "macros") {
    const rows = isEn
      ? [["Protein","119g","82%","bg-emerald-500"],["Carbs","159g","65%","bg-amber-500"],["Fat","53g","71%","bg-rose-500"]]
      : [["بروتين","119g","82%","bg-emerald-500"],["كارب","159g","65%","bg-amber-500"],["دهون","53g","71%","bg-rose-500"]];
    return (
      <div className="rounded-xl bg-emerald-50/60 border-2 border-emerald-500 p-3 space-y-1.5">
        {rows.map(([l,v,w,c])=>(
          <div key={l} className="flex items-center gap-2">
            <span className="text-[10px] text-neutral-600 w-12">{l}</span>
            <div className="flex-1 h-1.5 bg-white rounded-full overflow-hidden">
              <div className={`h-full ${c}`} style={{ width: w }} />
            </div>
            <span className="text-[10px] font-bold text-neutral-800 w-9 text-left">{v}</span>
          </div>
        ))}
      </div>
    );
  }
  if (kind === "water") {
    return (
      <div className="rounded-xl bg-sky-50/60 border-2 border-sky-500 p-3">
        <div className="text-center text-[10px] text-neutral-600 mb-1.5">{isEn ? "6 of 8 glasses" : "6 من 8 أكواب"}</div>
        <div className="flex items-end justify-center gap-1">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className={`w-4 h-8 rounded-b-md rounded-t-sm border-2 ${i < 6 ? "bg-sky-400 border-sky-500" : "bg-white border-sky-200"}`} />
          ))}
        </div>
      </div>
    );
  }
  if (kind === "exercise") {
    const rows = isEn
      ? [["Brisk walk","30 min","150 kcal"],["Weight training","45 min","320 kcal"]]
      : [["مشي سريع","30 د","150 kcal"],["وزن الحديد","45 د","320 kcal"]];
    return (
      <div className="rounded-xl bg-violet-50/60 border-2 border-violet-500 p-3 space-y-1.5">
        {rows.map(([n,d,k])=>(
          <div key={n} className="flex items-center gap-2 text-[11px]">
            <Dumbbell className="w-3 h-3 text-violet-600 shrink-0" />
            <span className="text-neutral-800 font-bold flex-1">{n}</span>
            <span className="text-neutral-500">{d}</span>
            <span className="text-violet-700 font-black">{k}</span>
          </div>
        ))}
      </div>
    );
  }
  if (kind === "steps") {
    return (
      <div className="rounded-xl bg-amber-50/60 border-2 border-amber-500 p-3">
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-2xl font-black text-amber-600">7,842</span>
          <span className="text-[10px] text-neutral-500">/ 10,000</span>
        </div>
        <div className="h-2 rounded-full bg-white overflow-hidden">
          <div className="h-full bg-gradient-to-r from-amber-400 to-amber-600" style={{ width: "78%" }} />
        </div>
      </div>
    );
  }
  // weight — mirrors the real /app "تطوّر الوزن" card style: white bg,
  // faint horizontal grid, orange data points connected by a curved
  // line, plus a dashed green goal line labelled "60.0". 7 days of data.
  const days = isEn
    ? ["Sun", "Sat", "Fri", "Thu", "Wed", "Tue", "Mon"]
    : ["أحد", "سبت", "جمعة", "خميس", "أربعاء", "ثلاثاء", "اثنين"];
  // Weights over the 7 days (right → left in RTL, so newest on the LEFT).
  const weights = [65.0, 64.4, 64.6, 63.8, 63.2, 62.5, 62.0];
  // Chart layout in a 240×110 viewBox.
  const yMin = 59, yMax = 66;                          // padding so 60 sits near the bottom, 65 near the top
  const xs = weights.map((_, i) => 20 + (i * (200 / (weights.length - 1))));
  const ys = weights.map((w) => 15 + ((yMax - w) / (yMax - yMin)) * 70);
  const linePath = xs.map((x, i) => `${i === 0 ? "M" : "L"}${x},${ys[i]}`).join(" ");
  const goalY = 15 + ((yMax - 60) / (yMax - yMin)) * 70;

  return (
    <div className="rounded-xl bg-white border-2 border-rose-500 p-3">
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-[10px] text-neutral-500">{isEn ? "7 days logged" : "7 أيام مسجّلة"}</span>
        <span className="text-[11px] font-black text-neutral-900">{isEn ? "Weight trend (kg)" : "تطوّر الوزن (كجم)"}</span>
      </div>
      <div className="relative">
        <svg viewBox="0 0 240 110" className="w-full h-24">
          {/* horizontal grid lines */}
          {[0, 1, 2, 3].map((i) => (
            <line key={i} x1="20" x2="230" y1={20 + i * 22} y2={20 + i * 22}
                  stroke="#f4f4f5" strokeWidth="1" />
          ))}
          {/* dashed green goal line */}
          <line x1="20" x2="230" y1={goalY} y2={goalY}
                stroke="#10b981" strokeWidth="2" strokeDasharray="5 4" strokeLinecap="round" />
          {/* weight trend line (orange) */}
          <path d={linePath} fill="none" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          {/* data points */}
          {xs.map((x, i) => (
            <circle key={i} cx={x} cy={ys[i]} r="3" fill="#f97316" stroke="white" strokeWidth="1.5" />
          ))}
          {/* value label on the highest weight (first point in RTL = right end) */}
          <text x={xs[0]} y={ys[0] - 6} textAnchor="middle"
                className="fill-brand-600" style={{ fontSize: "9px", fontWeight: 800 }}>
            {weights[0].toFixed(1)}
          </text>
        </svg>
        {/* goal pill anchored to the dashed line */}
        <div className="absolute right-0 flex items-center gap-1"
             style={{ top: `${(goalY / 110) * 100}%`, transform: "translateY(-50%)" }}>
          <span className="bg-emerald-50 text-emerald-700 text-[9px] font-black px-1.5 py-0.5 rounded-full border-2 border-emerald-500">
            60.0
          </span>
        </div>
      </div>
      {/* day labels (RTL: right → left) */}
      <div className="flex items-center justify-between px-4 mt-1 text-[9px] text-neutral-500">
        {days.map((d) => <span key={d}>{d}</span>)}
      </div>
    </div>
  );
}

function FeaturesTrackersOld() {
  // Continuous numbering: trackers are features 03 → 08 in the master
  // sequence. No group header here — each tracker owns its own feature
  // slot with a full <FeatureNumber> badge, so visitors read them the
  // same way they read Logging (01) and Recipes (02).
  return (
    <div className="mb-16 space-y-6">
      {TRACKERS.map(({ Icon, title, desc, color, mock }, i) => {
        const n = 3 + i; // 03 → 08
        return (
          <div key={title} className="rounded-3xl bg-white border-2 border-neutral-900 p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center">
              <div>
                <FeatureNumber n={n} />
                <div className="flex items-center gap-3 mb-3">
                  <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl border-2 ${COLOR_MAP[color]} shrink-0`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-neutral-900">{title}</h3>
                </div>
                <p className="text-sm md:text-base text-neutral-600 leading-relaxed max-w-lg">{desc}</p>
              </div>
              <div className="w-full md:w-64 shrink-0">
                <TrackerMock kind={mock} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// 3.4 — Progress
function FeaturesProgressOld() {
  return (
    <div className="mb-16">
      <div className="text-center mb-8">
        <FeatureNumber n={9} />
        <h3 className="text-xl sm:text-2xl font-black text-neutral-900 mb-2">
          شوف تقدمك بدل ما تعتمد على إحساسك
        </h3>
        <p className="text-sm text-neutral-600 max-w-xl mx-auto">
          الميزان مو دايم يوضح لك كل شيء. كالوفاي يساعدك تشوف التغيير من أكثر من زاوية.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ProgressCard Icon={TrendingUp} title="تابع تقدمك"
          desc="شوف توازن أسبوعك ومدى قربك من هدفك." tone="emerald">
          <ProgressChartMock />
        </ProgressCard>
        <ProgressCard Icon={Flame} title="حافظ على استمراريتك"
          desc="تابع الأيام اللي استمريت فيها وخلك محافظ على عادتك." tone="brand">
          <StreakMock />
        </ProgressCard>
        <ProgressCard Icon={Camera} title="صور قبل وبعد"
          desc="احفظ صور تقدمك وقارن بينها وشوف التغيير مع الوقت." tone="sky">
          <BeforeAfterMock />
        </ProgressCard>
        <ProgressCard Icon={Share2} title="شارك تقدمك مع مدربك"
          desc="أعطِ مدربك أو دكتور التغذية رابط خاص محمي بكلمة سر — يفتحه من جواله ويشوف وزنك وسعراتك وصور تقدمك أول بأول." tone="violet">
          <ShareLinkMock />
        </ProgressCard>
      </div>
    </div>
  );
}

function ProgressCard({ Icon, title, desc, children, tone }) {
  const toneMap = {
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
    brand:   "bg-brand-50 text-brand-600 border-brand-100",
    sky:     "bg-sky-50 text-sky-600 border-sky-100",
    violet:  "bg-violet-50 text-violet-600 border-violet-100",
  };
  return (
    <div className="rounded-2xl bg-white border-2 border-neutral-900 p-5 flex flex-col">
      <div className="flex items-start gap-3 mb-4">
        <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl border ${toneMap[tone]} shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h4 className="text-base font-bold text-neutral-900 mb-1">{title}</h4>
          <p className="text-sm text-neutral-600 leading-relaxed">{desc}</p>
        </div>
      </div>
      <div className="mt-auto">{children}</div>
    </div>
  );
}

function ProgressChartMock() {
  return (
    <div className="rounded-xl bg-gradient-to-b from-emerald-50/60 to-white p-3 border border-emerald-100">
      <div className="flex items-center justify-between text-[10px] text-neutral-500 mb-2">
        <span>-4.4 كجم</span><span>آخر 10 أسابيع</span>
      </div>
      <svg viewBox="0 0 200 60" className="w-full h-16">
        <path d="M0,15 L20,20 L40,25 L60,22 L80,32 L100,35 L120,38 L140,42 L160,45 L180,50 L200,52"
          fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" />
        <path d="M0,15 L20,20 L40,25 L60,22 L80,32 L100,35 L120,38 L140,42 L160,45 L180,50 L200,52 L200,60 L0,60 Z"
          fill="url(#g2)" opacity="0.25" />
        <defs>
          <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10b981" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      <div className="flex items-center justify-between text-[10px] text-neutral-500 mt-1">
        <span>82.5 كجم</span><span>78.1 كجم</span>
      </div>
    </div>
  );
}

function StreakMock() {
  const days = ["س", "ح", "ن", "ث", "ر", "خ", "ج"];
  const filled = [true, true, true, true, false, true, true];
  return (
    <div className="rounded-xl bg-gradient-to-b from-brand-50/60 to-white p-3 border border-brand-100">
      <div className="flex items-center gap-2 mb-2">
        <Flame className="w-4 h-4 text-brand-500" />
        <span className="text-sm font-black text-neutral-900">6</span>
        <span className="text-[11px] text-neutral-600">أيام متتالية</span>
      </div>
      <div className="flex items-center gap-1">
        {days.map((d, i) => (
          <div key={i} className={`flex-1 h-8 rounded-md flex items-center justify-center text-[10px] font-bold
            ${filled[i] ? "bg-brand-500 text-white" : "bg-neutral-100 text-neutral-400"}`}>
            {d}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Before / after visual — real AI-generated photos of the same
 *  Gulf/Khaleeji man in matching studio side profile: overweight →
 *  lean, same outfit (plain white t-shirt + black cotton trousers)
 *  on a neutral beige backdrop. Photos live in /public/. */
function BeforeAfterMock() {
  return (
    <div className="rounded-xl overflow-hidden border-2 border-neutral-900 aspect-[16/9] relative bg-neutral-100">
      {/* In RTL, first grid child renders on the right, second on the left.
          Order below: BEFORE first → right; AFTER second → left. */}
      <div className="absolute inset-0 grid grid-cols-2">
        {/* BEFORE — right */}
        <div className="relative overflow-hidden">
          <img
            src="/before-after-before.webp"
            alt="قبل — الرجل الخليجي بوزنه الأعلى"
            loading="lazy"
            draggable={false}
            className="w-full h-full object-cover"
            style={{ objectPosition: "50% center" }}
          />
          <div className="absolute top-2 right-2 bg-rose-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow-md border-2 border-white">
            قبل
          </div>
          <div className="absolute bottom-2 right-2 bg-white/95 text-rose-600 text-[10px] font-black px-2 py-0.5 rounded-full shadow-md border border-rose-500">
            95 كجم
          </div>
        </div>

        {/* AFTER — left */}
        <div className="relative overflow-hidden">
          <img
            src="/before-after-after.webp"
            alt="بعد — نفس الرجل بجسم رياضي"
            loading="lazy"
            draggable={false}
            className="w-full h-full object-cover"
            style={{ objectPosition: "78% center" }}
          />
          <div className="absolute top-2 left-2 bg-emerald-600 text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow-md border-2 border-white">
            بعد
          </div>
          <div className="absolute bottom-2 left-2 bg-white/95 text-emerald-700 text-[10px] font-black px-2 py-0.5 rounded-full shadow-md border border-emerald-600">
            78 كجم
          </div>
        </div>
      </div>

      {/* Divider + drag handle */}
      <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[3px] bg-white shadow-md" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-9 h-9 rounded-full bg-white border-2 border-neutral-900 shadow-md">
        <ChevronLeft className="w-3.5 h-3.5 text-neutral-700" />
        <ChevronLeft className="w-3.5 h-3.5 text-neutral-700 -mr-1 rotate-180" />
      </div>
    </div>
  );
}

function ShareLinkMock() {
  return (
    <div className="rounded-xl bg-neutral-900 border-2 border-neutral-900 p-3 text-white">
      {/* Link row */}
      <div className="flex items-center gap-2 text-[11px] mb-2">
        <Lock className="w-3 h-3 text-emerald-400 shrink-0" />
        <span className="text-neutral-400 truncate">calofay.app/share/x8k4…</span>
      </div>
      {/* Fake "viewer" strip — trainer just opened the link */}
      <div className="flex items-center gap-2 bg-white/[0.06] rounded-lg px-2 py-1.5 border border-white/10">
        <div className="w-6 h-6 rounded-full bg-brand-500 text-white text-[10px] font-black flex items-center justify-center shrink-0">
          م
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] text-white font-bold leading-tight">مدربي فتح الرابط</div>
          <div className="text-[9px] text-white/50 leading-tight">قبل 3 دقائق</div>
        </div>
        <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" strokeWidth={3} />
      </div>
    </div>
  );
}

// 3.5 — Help (AI + Nutritionist)
function FeaturesHelpOld() {
  return (
    <div className="mb-16">
      <div className="text-center mb-8">
        <FeatureNumber n={5} />
        <h3 className="text-xl sm:text-2xl font-black text-neutral-900 mb-2">
          لما تحتاج مساعدة… كالوفاي معك
        </h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-3xl bg-white border-2 border-neutral-900 p-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-violet-50 text-violet-600 border border-violet-100 mb-4">
            <Bot className="w-5 h-5" />
          </div>
          <h4 className="text-lg font-black text-neutral-900 mb-2">مساعد كالوفاي بالذكاء الاصطناعي</h4>
          <p className="text-sm text-neutral-600 leading-relaxed mb-4">
            مو مجرد شات عادي. مساعد كالوفاي يعرف هدفك وسعراتك وماكروزك والمتبقي لك خلال يومك عشان يعطيك اقتراحات مرتبطة بهدفك.
          </p>
          <div className="space-y-2">
            {[
              "باقي لي 400 سعرة و25 جرام بروتين، وش أقدر آكل؟",
              "أعطني عشاء سريع وعالي بالبروتين.",
              "وش البديل الأقل سعرات لهذا الأكل؟",
              "أعطني وصفة تناسب المتبقي لي اليوم.",
            ].map((q) => (
              <div key={q} className="text-xs text-neutral-700 bg-violet-50/60 border border-violet-100 rounded-lg px-3 py-2">
                “{q}”
              </div>
            ))}
          </div>
          <p className="text-xs text-neutral-500 mt-4">وتقدر تسأله بصوتك بدل ما تكتب.</p>
        </div>
        <div className="rounded-3xl bg-white border-2 border-neutral-900 p-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100 mb-4">
            <MessageCircle className="w-5 h-5" />
          </div>
          <h4 className="text-lg font-black text-neutral-900 mb-2">أخصائية تغذية عبر واتساب</h4>
          <p className="text-sm text-neutral-600 leading-relaxed mb-4">
            ولما تحتاج شخص حقيقي، تقدر تتواصل مع أخصائية التغذية عبر واتساب.
          </p>
          <div className="space-y-2">
            {[
              "مساعدة في فهم السعرات والماكروز",
              "أسئلة عن أكلك وهدفك",
              "مساعدة في استخدام كالوفاي",
              "دعم بشري عند الحاجة",
            ].map((k) => <CheckLine key={k}>{k}</CheckLine>)}
          </div>
        </div>
      </div>
    </div>
  );
}

// 3.6 — Traditional vs Calofay
function FeaturesCompare({ onStart }) {
  return null; // Superseded by <DifferentSection /> — kept as an intentional no-op stub in case
  // an older reference still imports it. Safe to delete once you've confirmed nothing links to it.
}

// ═════════════════════════════════════════════════════════════════════
// 4 · HOW TO START + GUARANTEE
// ═════════════════════════════════════════════════════════════════════

const STEPS = [
  { n: "1",
    title: "قل لنا هدفك",
    title_en: "Tell us your goal",
    desc: "هل تبغى تنزل وزن، تبني عضل، أو تحافظ على جسمك؟ جاوب على أسئلة بسيطة عن جسمك ونشاطك وهدفك.",
    desc_en: "Losing weight, building muscle, or holding steady? Answer a few quick questions about your body, activity, and goal." },
  { n: "2",
    title: "نجهز لك خطتك",
    title_en: "We build your plan",
    desc: "كالوفاي يحسب لك احتياجك اليومي من السعرات والبروتين والكارب والدهون حسب هدفك بالضبط.",
    desc_en: "Calofay dials in your daily calorie, protein, carb, and fat targets — precisely for your goal." },
  { n: "3",
    title: "سجل يومك بسهولة",
    title_en: "Log your day, effortlessly",
    desc: "صور وجبتك، أو اشرحها للذكاء الاصطناعي، وتابع أكلك ووزنك وتقدمك يوم بعد يوم.",
    desc_en: "Snap a photo of your meal or just describe it to the AI — then watch your food, weight, and progress line up day by day." },
];

const GUARANTEE_BULLETS = [
  "جميع المميزات من أول يوم",
  "ضمان استرجاع كامل لمدة 15 يوم",
  "بياناتك وصورك خاصة",
  "دفع آمن",
];

function HowToStart({ onStart }) {
  const { t, lang } = useLandingLang();
  return (
    <DarkSection testid="lv2-how">
      <div className="max-w-5xl mx-auto">
        <SectionBubble
          dark
          eyebrow={t("how.eyebrow")}
          title={t("how.title")}
          testid="lv2-how-header"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {STEPS.map((s) => (
            <div key={s.n} className="relative rounded-3xl bg-white/[0.04] backdrop-blur border-2 border-white/70 p-6 pt-9">
              <div className="absolute -top-4 right-6 orange-gradient text-white text-lg font-black w-9 h-9 rounded-full flex items-center justify-center shadow-lg">
                {s.n}
              </div>
              <h4 className="text-lg font-bold text-white mb-2">{lang === "en" ? s.title_en : s.title}</h4>
              <p className="text-sm text-white/70 leading-relaxed">{lang === "en" ? s.desc_en : s.desc}</p>
            </div>
          ))}
        </div>
        <div className="text-center">
          <PillCta onClick={onStart} testid="lv2-how-cta">{t("how.cta")}</PillCta>
        </div>
      </div>
    </DarkSection>
  );
}

// ═════════════════════════════════════════════════════════════════════
// 4.5 · GUARANTEE — dedicated trust section. Uses emerald theme so it
// visually breaks from the surrounding orange/dark rhythm and reads as
// a promise, not a feature. Positioned before the price to remove the
// last risk barrier right before the ask.
// ═════════════════════════════════════════════════════════════════════

function GuaranteeSection({ onStart }) {
  const { t, isRTL } = useLandingLang();
  return (
    <LightSection testid="lv2-guarantee">
      <div className="max-w-4xl mx-auto">
        <div className="relative rounded-[2rem] bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40 border-2 border-emerald-600 p-6 md:p-10 shadow-floating">
          {/* Shield badge — fully INSIDE the card (was `-top-8` and rendering
              half-clipped on Safari + Android Chrome). Sits above the pill
              with breathing room; the whole card now stops right after the
              CTA so there's no dead space at the bottom. */}
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-emerald-600 text-white flex items-center justify-center shadow-floating border-4 border-white">
              <ShieldCheck className="w-7 h-7 md:w-8 md:h-8" strokeWidth={2.5} />
            </div>
          </div>

          <div className="text-center" dir={isRTL ? "rtl" : "ltr"}>
            <div className="inline-flex items-center gap-1.5 bg-emerald-600 text-white text-xs font-black px-3 py-1.5 rounded-full mb-3">
              {t("guar.badge")}
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-neutral-900 leading-[1.15] mb-2">
              {t("guar.headline_pre")}<span className="text-emerald-600">{t("guar.headline_days")}</span>
            </h2>
            <p className="text-sm md:text-base font-bold text-emerald-700 mb-5">
              {t("guar.headline_sub")}
            </p>
            <p className="text-base md:text-lg text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              {t("guar.body_1")}
              <br /><br />
              {t("guar.body_2")}
              <br /><br />
              {t("guar.body_3")}
              <br /><br />
              <b className="text-neutral-900">{t("guar.body_close")}</b>
            </p>

            {/* Saudi-pride badge — placed right under the closing
                promise line as a subtle emotional cue that this is a
                homegrown brand. Uses Saudi green (#006C35) with a
                gentle gradient + the flag emoji rendered in an inner
                pill for crispness on all devices. */}
            <div className="flex justify-center mb-6" data-testid="saudi-pride-badge">
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-xs sm:text-sm font-black shadow-md"
                style={{
                  background:
                    "linear-gradient(135deg, #007a3d 0%, #006c35 55%, #00552a 100%)",
                  border: "1px solid rgba(255,255,255,0.25)",
                  boxShadow:
                    "0 6px 18px -6px rgba(0,108,53,0.45), inset 0 1px 0 rgba(255,255,255,0.15)",
                }}
              >
                <span className="text-lg leading-none" aria-hidden>🇸🇦</span>
                <span className="tracking-wide">{t("guar.saudi_pride")}</span>
              </div>
            </div>

            <PillCta onClick={onStart} testid="lv2-guarantee-cta">{t("guar.cta")}</PillCta>
          </div>
        </div>
      </div>
    </LightSection>
  );
}

// ═════════════════════════════════════════════════════════════════════
// 5 · PRICE / VALUE
// ═════════════════════════════════════════════════════════════════════

const APPS_IN_1 = [
  { Icon: Camera,       title: "تسجيل بـ 5 طرق",           desc: "صور · صوت · باركود · مفضلة · يدوي." },
  { Icon: ChefHat,      title: "+500 وصفة عربية",          desc: "مع المكونات وطريقة التحضير والسعرات." },
  { Icon: Import,       title: "استيراد من فيديو",         desc: "TikTok و Instagram Reels بضغطة زر." },
  { Icon: ShoppingCart, title: "قائمة تسوق ذكية",           desc: "من وصفاتك · مرتبة حسب الأقسام." },
  { Icon: Flame,        title: "حساب السعرات",             desc: "كم تحتاج، كم أكلت، كم متبقي." },
  { Icon: BarChart3,    title: "الماكروز",                 desc: "بروتين · كارب · دهون." },
  { Icon: Droplets,     title: "الماء",                    desc: "هدف يومي + تذكيرات." },
  { Icon: Dumbbell,     title: "التمارين",                 desc: "+100 نوع · حساب السعرات المحروقة." },
  { Icon: Footprints,   title: "الخطوات",                  desc: "يومي + ربط مع تطبيق الصحة." },
  { Icon: Scale,        title: "الوزن + التقدم",           desc: "مخطط أسبوعي وشهري." },
  { Icon: Camera,       title: "صور قبل / بعد",             desc: "قارن التغيير جنب بعض." },
  { Icon: Share2,       title: "شارك مع مدربك",             desc: "رابط خاص محمي بكلمة سر." },
  { Icon: Bot,          title: "مدرب AI عربي",              desc: "يعرف هدفك وسعراتك." },
  { Icon: MessageCircle, title: "أخصائية تغذية · واتساب",   desc: "دعم بشري لما تحتاجه." },
];

const SUB_BULLETS = [
  { ar: "تسجيل الأكل بـ 5 طرق (صور · صوت · باركود · مفضلة · يدوي)", en: "Log food 5 ways (photo · voice · barcode · favorites · manual)" },
  { ar: "أكثر من 500 وصفة عربية جاهزة",                               en: "500+ ready-to-cook recipes with full macros" },
  { ar: "استيراد وصفة من TikTok / Instagram",                        en: "Import recipes from TikTok / Instagram" },
  { ar: "قائمة تسوق ذكية تلقائية",                                     en: "Auto-built smart shopping list" },
  { ar: "حساب السعرات اليومي",                                          en: "Daily calorie tracking" },
  { ar: "متابعة الماكروز (بروتين · كارب · دهون)",                    en: "Macro tracking (protein · carbs · fat)" },
  { ar: "تتبّع الماء + تذكيرات",                                       en: "Water tracking + reminders" },
  { ar: "تسجيل التمارين + حساب السعرات المحروقة",                     en: "Workout logging + calories-burned" },
  { ar: "متابعة الخطوات + ربط مع تطبيق الصحة",                        en: "Step tracking + Apple Health sync" },
  { ar: "متابعة الوزن مع مخطط اتجاهي",                                en: "Weight tracking with trend chart" },
  { ar: "صور قبل / بعد للتقدم",                                        en: "Before/after progress photos" },
  { ar: "مشاركة تقدمك مع مدربك برابط خاص",                            en: "Share progress with your coach via private link" },
  { ar: "مدرب كالوفاي بالذكاء الاصطناعي",                             en: "Calofay's personal AI coach" },
  { ar: "أخصائية تغذية عبر واتساب",                                    en: "WhatsApp support team" },
  { ar: "دفتر يومي (يومياتك + قائمة مهامك)",                         en: "Daily journal (mood + to-do list)" },
  { ar: "تحديثات مستمرة ومميزات جديدة",                                en: "Ongoing updates and new features" },
  { ar: "ضمان استرجاع كامل لمدة 15 يوم",                              en: "Full 15-day money-back guarantee" },
];

function PriceValue({ onStart, pricing }) {
  const { t } = useLandingLang();
  return (
    <DarkSection testid="lv2-price">
      <div className="max-w-6xl mx-auto">
        <SectionBubble
          dark
          eyebrow={t("price.eyebrow")}
          title={t("price.title")}
          sub={t("price.sub")}
          testid="lv2-price-header"
        />
        <PriceCard onStart={onStart} pricing={pricing} />
      </div>
    </DarkSection>
  );
}

function PriceCard({ onStart, pricing }) {
  const { t, lang, isRTL } = useLandingLang();
  const sym = displaySymbol(pricing, lang);
  const perDay = pricing.country === "SA"
    ? t("price.perday_sa")
    : (lang === "en"
        ? ` · about ${pricing.perDay} ${sym} a day`
        : ` · حوالي ${pricing.perDay} ${sym} باليوم`);
  return (
    <div className="max-w-2xl mx-auto" data-testid="lv2-price-card-wrap">
      {/* Luxurious black card — gold is now reserved for the two focal
          "premium cue" spots (PREMIUM pill + big price number). Every
          other accent uses the brand orange so the card belongs to the
          same visual family as the rest of the site. */}
      <div
        className="relative rounded-3xl p-6 md:p-9 shadow-2xl overflow-hidden"
        style={{
          background:
            "linear-gradient(155deg, #0a0a0a 0%, #171717 45%, #0f0f0f 100%)",
          boxShadow:
            "0 25px 60px -20px rgba(0,0,0,0.55), 0 0 0 1px rgba(249,115,22,0.35) inset, 0 0 30px -10px rgba(249,115,22,0.30)",
        }}
        data-testid="lv2-price-card"
      >
        {/* Brand-orange ambient glow — top right */}
        <div
          className="absolute -top-24 -right-24 w-72 h-72 rounded-full pointer-events-none opacity-60"
          style={{
            background:
              "radial-gradient(circle, rgba(249,115,22,0.35), rgba(249,115,22,0) 65%)",
          }}
        />
        {/* Brand-orange ambient glow — bottom left */}
        <div
          className="absolute -bottom-32 -left-24 w-80 h-80 rounded-full pointer-events-none opacity-40"
          style={{
            background:
              "radial-gradient(circle, rgba(249,115,22,0.28), rgba(249,115,22,0) 70%)",
          }}
        />
        {/* Brand-orange border hairline on top */}
        <div
          className="absolute top-0 left-0 right-0 h-px pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(249,115,22,0.9), transparent)",
          }}
        />

        <div className="relative">
          {/* "PREMIUM" gold pill — GOLD KEPT here as the primary
              "this is luxurious" cue. */}
          <div className="flex justify-center mb-5">
            <div
              className="inline-flex items-center gap-1.5 text-[10px] font-black tracking-[0.25em] px-4 py-1.5 rounded-full"
              style={{
                background:
                  "linear-gradient(135deg, #f7d97b 0%, #d4af37 50%, #a8842c 100%)",
                color: "#0a0a0a",
                boxShadow: "0 4px 14px -4px rgba(212,175,55,0.55)",
              }}
            >
              <Sparkles className="w-3 h-3" strokeWidth={2.5} />
              {t("price.premium")}
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-5">
            <div className="text-xs font-bold text-white/60 tracking-widest uppercase mb-3">
              {t("price.plan_label")}
            </div>
            <div className="flex items-baseline justify-center gap-2">
              {/* Big price — GOLD KEPT here as the second focal
                  luxury cue. Everything else in the card is orange. */}
              <div
                className="text-5xl sm:text-6xl font-black leading-none"
                style={{
                  backgroundImage:
                    "linear-gradient(180deg, #fbe38a 0%, #d4af37 55%, #b58c2f 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                  WebkitTextFillColor: "transparent",
                }}
                data-testid="price-per-month"
              >
                {pricing.monthly}
              </div>
              <div className="text-xl font-black text-white/85">{sym}</div>
              <div className="text-sm font-bold text-white/50">{t("price.per_month")}</div>
            </div>
            <div className="text-[11px] text-white/55 mt-2 font-medium" data-testid="price-yearly-note">
              {t("price.yearly_pre")}{pricing.yearly} {sym}{t("price.yearly_per_year")}
              {perDay}
            </div>
          </div>

          {/* Divider with brand-orange diamond */}
          <div className="flex items-center gap-3 mb-5">
            <div
              className="flex-1 h-px"
              style={{
                background:
                  "linear-gradient(to left, transparent, rgba(249,115,22,0.5), transparent)",
              }}
            />
            <div
              className="w-1.5 h-1.5 rotate-45"
              style={{ background: "linear-gradient(135deg, #fb923c, #ea580c)" }}
            />
            <div
              className="flex-1 h-px"
              style={{
                background:
                  "linear-gradient(to right, transparent, rgba(249,115,22,0.5), transparent)",
              }}
            />
          </div>

          {/* Feature list */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-[11px] font-black text-white uppercase tracking-[0.2em]">
                {t("price.features_head")}
              </span>
              <span
                className="text-[9px] font-black px-2.5 py-1 rounded-full"
                style={{
                  background: "rgba(249,115,22,0.15)",
                  color: "#fb923c",
                  border: "1px solid rgba(249,115,22,0.40)",
                }}
              >
                {SUB_BULLETS.length - 2} {t("price.feature_count")}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2">
              {SUB_BULLETS.slice(0, -2).map((b) => (
                <div
                  key={b.ar}
                  className="flex items-start gap-2.5 text-[13px] text-white/85 leading-snug py-0.5"
                >
                  <span
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full shrink-0 mt-0.5"
                    style={{
                      background:
                        "linear-gradient(135deg, #fb923c 0%, #ea580c 100%)",
                    }}
                  >
                    <Check className="w-2.5 h-2.5 text-white" strokeWidth={4} />
                  </span>
                  <span>{lang === "en" ? b.en : b.ar}</span>
                </div>
              ))}
            </div>
            {/* Perks — separated by orange hairline */}
            <div
              className="mt-4 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2"
              style={{
                borderTop: "1px solid rgba(249,115,22,0.25)",
              }}
            >
              {SUB_BULLETS.slice(-2).map((b) => (
                <div
                  key={b.ar}
                  className="flex items-start gap-2.5 text-[13px] font-bold leading-snug py-0.5"
                  style={{ color: "#fed7aa" }}
                >
                  <span
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full shrink-0 mt-0.5"
                    style={{
                      background:
                        "linear-gradient(135deg, #fb923c 0%, #ea580c 100%)",
                    }}
                  >
                    <Check className="w-2.5 h-2.5 text-white" strokeWidth={4} />
                  </span>
                  <span>{lang === "en" ? b.en : b.ar}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Brand-orange CTA — same gradient family as Landing hero */}
          <button
            onClick={onStart}
            data-testid="lv2-price-cta"
            className="group relative w-full font-black text-base py-4 rounded-2xl active:scale-[0.98] transition-all inline-flex items-center justify-center gap-2 overflow-hidden text-white"
            style={{
              background:
                "linear-gradient(135deg, #fb923c 0%, #f97316 45%, #ea580c 100%)",
              boxShadow:
                "0 12px 30px -8px rgba(249,115,22,0.55), 0 0 0 1px rgba(255,255,255,0.15) inset",
            }}
          >
            <span className="relative z-10">{t("price.cta")}</span>
            <ChevronLeft className={`w-4 h-4 relative z-10 ${isRTL ? "" : "rotate-180"}`} strokeWidth={3} />
            {/* Shine sweep */}
            <div
              className="absolute inset-y-0 -left-full w-1/2 skew-x-[-20deg] group-hover:left-full transition-all duration-700"
              style={{
                background:
                  "linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)",
              }}
            />
          </button>

          {/* Footnote */}
          <p className="text-[11px] text-white/55 text-center mt-4 leading-relaxed">
            {t("price.footnote_pre")}<b className="text-amber-300">{t("price.footnote_days")}</b>{t("price.footnote_post")}
          </p>
        </div>
      </div>

      {/* Trust row */}
      <div className="flex items-center justify-center gap-6 text-xs text-white/60 mt-5">
        <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> {t("price.trust_pay")}</span>
        <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> {t("price.trust_guar")}</span>
        <span className="inline-flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" /> {t("price.trust_support")}</span>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// 5.5 · COMPETITORS COMPARISON — "مميزات ما راح تلقاها عند المنافسين"
// Dark section, alternates against the light PriceValue above.
// Feature list intentionally reflects what Calofay actually delivers
// today; ✓ = full support, ~ = partial/paid-add-on, ✗ = missing.
// ═════════════════════════════════════════════════════════════════════

const RIVAL_ROWS = [
  { f: "واجهة عربية أصلية",                                  f_en: "Native Arabic interface",                                          us: "yes", mfp: "no",  lose: "no"  },
  { f: "تصوير الطعام بالذكاء الاصطناعي",                    f_en: "AI food photo recognition",                                        us: "yes", mfp: "yes", lose: "yes" },
  { f: "تسجيل الأكل بالصوت العربي",                          f_en: "Voice logging in Arabic",                                          us: "yes", mfp: "no",  lose: "no"  },
  { f: "+500 وصفة عربية جاهزة",                              f_en: "500+ ready-to-cook Arabic recipes",                                us: "yes", mfp: "no",  lose: "no"  },
  { f: "استيراد وصفة من رابط انستقرام أو تيكتوك",           f_en: "Import a recipe from an Instagram or TikTok link",                 us: "yes", mfp: "no",  lose: "no"  },
  { f: "إنشاء قائمة تسوق تلقائية من الوصفات",               f_en: "Auto-generated shopping list from your recipes",                   us: "yes", mfp: "no",  lose: "no"  },
  { f: "مساعد ذكاء اصطناعي عربي يعرف هدفك",                f_en: "AI coach that already knows your goal",                            us: "yes", mfp: "no",  lose: "no"  },
  { f: "تواصل مع أخصائية تغذية عبر واتساب",                 f_en: "Chat with our support team on WhatsApp",                           us: "yes", mfp: "no",  lose: "no"  },
  { f: "مقارنة صورك قبل وبعد بالتواريخ",                    f_en: "Compare before/after photos with dates",                           us: "yes", mfp: "no",  lose: "no"  },
  { f: "مشاركة معلومات حسابك مع مدربك الشخصي برابط محمي",  f_en: "Share your progress with your coach via a password-protected link", us: "yes", mfp: "no",  lose: "no"  },
  { f: "دفتر يومي (يومياتك + قائمة مهامك)",                 f_en: "Daily journal (mood log + to-do list)",                            us: "yes", mfp: "no",  lose: "no"  },
  { f: "تتبّع الوزن والماء والخطوات",                        f_en: "Weight, water, and step tracking",                                 us: "yes", mfp: "yes", lose: "yes" },
];

function Mark({ kind, light = false }) {
  if (kind === "yes") {
    return (
      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${light ? "bg-emerald-500 text-white" : "bg-emerald-500/20 text-emerald-400"}`}>
        <Check className="w-3.5 h-3.5" strokeWidth={3} />
      </span>
    );
  }
  if (kind === "partial") {
    return <span className={`text-xs font-bold ${light ? "text-amber-600" : "text-amber-300"}`}>~</span>;
  }
  return (
    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${light ? "bg-rose-100 text-rose-400" : "bg-white/5 text-white/30"}`}>
      <X className="w-3.5 h-3.5" />
    </span>
  );
}

function CompetitorsSection({ onStart }) {
  const { t, lang } = useLandingLang();
  return (
    <LightSection testid="lv2-rivals">
      <div className="max-w-5xl mx-auto">
        <SectionBubble
          eyebrow={t("riv.eyebrow")}
          title={t("riv.title")}
          sub={t("riv.sub")}
          testid="lv2-rivals-header"
        />

        {/* Desktop / tablet table */}
        <div className="hidden md:block relative">
          <div
            aria-hidden
            className="absolute -inset-6 pointer-events-none rounded-[36px]"
            style={{
              background:
                "radial-gradient(closest-side, rgba(249,115,22,0.35), rgba(249,115,22,0) 70%)",
              filter: "blur(24px)",
            }}
          />
          <div
            className="relative rounded-3xl border border-brand-500/25 overflow-hidden"
            style={{
              background:
                "linear-gradient(180deg, #0b0b0d 0%, #050506 100%)",
              boxShadow:
                "0 30px 60px -20px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
            }}
            data-testid="rivals-table-desktop"
          >
            {/* Header row */}
            <div className="grid grid-cols-[1.6fr_repeat(3,minmax(0,1fr))] text-sm font-bold">
              <div className="px-5 py-5 text-neutral-500 text-sm border-b border-white/10">{t("riv.col_feature")}</div>
              <div className="px-3 py-4 text-center flex flex-col items-center justify-center gap-1.5 bg-brand-500 relative overflow-hidden"
                style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.10)" }}>
                <span className="bg-white text-brand-600 text-[10px] font-black px-2.5 py-0.5 rounded-full shadow-md whitespace-nowrap">
                  {t("riv.best_badge")}
                </span>
                <div className="text-white font-black text-base drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">Calofay</div>
              </div>
              <div className="px-3 py-5 text-center border-b border-white/10">
                <div className="text-neutral-300 text-base">MyFitnessPal</div>
              </div>
              <div className="px-3 py-5 text-center border-b border-white/10">
                <div className="text-neutral-300 text-base">Lose It!</div>
              </div>
            </div>
            {/* Data rows */}
            {RIVAL_ROWS.map((row, i) => {
              const notLast = i < RIVAL_ROWS.length - 1;
              const b = notLast ? "border-b border-white/[0.06]" : "";
              return (
                <div key={row.f} className="grid grid-cols-[1.6fr_repeat(3,minmax(0,1fr))] text-[15px] items-stretch">
                  <div className={`px-5 py-4 text-neutral-100 font-medium flex items-center ${b}`}>{lang === "en" ? row.f_en : row.f}</div>
                  {/* Calofay column: solid orange, NO border — blends with cells above/below */}
                  <div className="px-3 py-4 flex items-center justify-center bg-brand-500">
                    {row.us === "yes" ? (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/25 text-white ring-1 ring-white/40">
                        <Check className="w-3.5 h-3.5" strokeWidth={3} />
                      </span>
                    ) : row.us === "partial" ? (
                      <span className="text-xs font-bold text-white">~</span>
                    ) : (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/10 text-white/60">
                        <X className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </div>
                  <div className={`px-3 py-4 flex items-center justify-center ${b}`}>
                    <Mark kind={row.mfp} />
                  </div>
                  <div className={`px-3 py-4 flex items-center justify-center ${b}`}>
                    <Mark kind={row.lose} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile — same pattern, tighter grid */}
        <div className="md:hidden relative">
          <div
            aria-hidden
            className="absolute -inset-4 pointer-events-none rounded-[28px]"
            style={{
              background:
                "radial-gradient(closest-side, rgba(249,115,22,0.35), rgba(249,115,22,0) 70%)",
              filter: "blur(18px)",
            }}
          />
          <div
            className="relative rounded-2xl border border-brand-500/25 overflow-hidden"
            style={{
              background:
                "linear-gradient(180deg, #0b0b0d 0%, #050506 100%)",
              boxShadow:
                "0 20px 40px -15px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
            }}
            data-testid="rivals-table-mobile"
          >
            {/* Header row */}
            <div className="grid grid-cols-[1.7fr_1.1fr_repeat(2,minmax(0,1fr))] text-xs font-bold">
              <div className="px-2.5 py-4 text-neutral-500 text-[11px] flex items-center border-b border-white/10">{t("riv.col_feature")}</div>
              <div className="px-1 py-3 text-center flex flex-col items-center justify-center gap-1 bg-brand-500 relative"
                style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.10)" }}>
                <span className="bg-white text-brand-600 text-[8.5px] font-black px-1.5 py-0.5 rounded-full whitespace-nowrap shadow-sm">
                  {t("riv.best_badge")}
                </span>
                <div className="text-white font-black text-[13px] leading-none drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">Calofay</div>
              </div>
              <div className="px-1 py-4 text-center flex items-center justify-center border-b border-white/10">
                <div className="text-neutral-300 text-[10px] leading-tight font-bold whitespace-nowrap">MyFitnessPal</div>
              </div>
              <div className="px-1 py-4 text-center flex items-center justify-center border-b border-white/10">
                <div className="text-neutral-300 text-[11.5px] leading-tight font-bold">Lose It!</div>
              </div>
            </div>
            {/* Data rows */}
            {RIVAL_ROWS.map((row, i) => {
              const notLast = i < RIVAL_ROWS.length - 1;
              const b = notLast ? "border-b border-white/[0.06]" : "";
              return (
                <div key={row.f} className="grid grid-cols-[1.7fr_1.1fr_repeat(2,minmax(0,1fr))] text-xs items-stretch">
                  <div className={`px-2.5 py-3 text-neutral-100 text-[12px] leading-snug font-medium flex items-center ${b}`}>{lang === "en" ? row.f_en : row.f}</div>
                  <div className="px-1 py-3 flex items-center justify-center bg-brand-500">
                    {row.us === "yes" ? (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/25 text-white ring-1 ring-white/40">
                        <Check className="w-3.5 h-3.5" strokeWidth={3} />
                      </span>
                    ) : row.us === "partial" ? (
                      <span className="text-xs font-bold text-white">~</span>
                    ) : (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/10 text-white/60">
                        <X className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </div>
                  <div className={`px-1 py-3 flex items-center justify-center ${b}`}>
                    <Mark kind={row.mfp} />
                  </div>
                  <div className={`px-1 py-3 flex items-center justify-center ${b}`}>
                    <Mark kind={row.lose} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-10 text-center">
          <p className="text-base text-neutral-700 max-w-xl mx-auto leading-relaxed mb-5">
            {t("riv.close")}
          </p>
          <PillCta onClick={onStart} testid="lv2-rivals-cta">{t("riv.cta")}</PillCta>
        </div>
      </div>
    </LightSection>
  );
}

// ═════════════════════════════════════════════════════════════════════
// 6 · FAQ + FINAL CTA
// ═════════════════════════════════════════════════════════════════════

function FAQ({ pricing }) {
  const [open, setOpen] = useState(-1);
  const { t, lang, isRTL } = useLandingLang();
  return (
    <DarkSection testid="lv2-faq">
      <div className="max-w-3xl mx-auto">
        <SectionBubble
          dark
          eyebrow={t("faq.eyebrow")}
          title={t("faq.title")}
          testid="lv2-faq-header"
        />
        <div className="space-y-4">
          {FAQ_ITEMS.map((item, i) => {
            const isOpen = open === i;
            const q = lang === "en" ? (item.q_en || item.q) : item.q;
            const rawA = lang === "en" ? (item.a_en || item.a) : item.a;
            const answer = typeof rawA === "function" ? rawA({ pricing }) : rawA;
            return (
              <div
                key={item.q}
                className={`rounded-3xl overflow-hidden transition-all duration-300 ${
                  isOpen
                    ? "bg-white/[0.05] border border-brand-500/40"
                    : "bg-white/[0.02] border border-white/10 hover:border-brand-500/25"
                }`}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`lv2-faq-${i}`}
                  className={`w-full flex items-center gap-4 ${isRTL ? "text-right" : "text-left"} px-5 md:px-6 py-5 md:py-6`}
                >
                  {/* Round + / – icon */}
                  <span
                    className={`shrink-0 w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isOpen
                        ? "bg-brand-500 text-white rotate-45"
                        : "bg-brand-500/15 text-brand-400 border border-brand-500/30"
                    }`}
                    aria-hidden
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </span>

                  {/* Question */}
                  <span className={`flex-1 ${isRTL ? "text-right" : "text-left"} text-[13px] md:text-sm font-bold text-white leading-snug`}>
                    {q}
                  </span>
                </button>

                {/* Answer — reveal panel */}
                <div
                  className={`grid transition-all duration-300 ${
                    isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className={`px-5 md:px-6 pb-5 md:pb-6 pt-0 -mt-1 ${isRTL ? "pr-16 md:pr-[70px] text-right" : "pl-16 md:pl-[70px] text-left"} text-[14px] md:text-[15px] text-white/75 leading-relaxed`}>
                      {answer}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </DarkSection>
  );
}

function FinalCta({ onStart, pricing }) {
  const { t, lang, isRTL } = useLandingLang();
  const sym = displaySymbol(pricing, lang);
  const perDayCopy = pricing.country === "SA" && lang === "ar"
    ? "حوالي 68 هللة باليوم — أقل من سعر الموية اللي تشتريها بريال."
    : pricing.country === "SA" && lang === "en"
      ? "That's about $0.20 a day — less than a bottle of water."
      : lang === "ar"
        ? `حوالي ${pricing.perDay} ${sym} باليوم فقط.`
        : `Only about ${pricing.perDay} ${sym} a day.`;
  return (
    <section className="orange-gradient py-16 md:py-20 px-4 text-white text-center" dir={isRTL ? "rtl" : "ltr"} data-testid="lv2-final">
      <div className="max-w-2xl mx-auto">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-3 leading-tight">
          {t("final.headline")}
        </h2>
        <p className="text-sm sm:text-base text-white/90 mb-2 leading-relaxed">
          {t("final.sub")}
        </p>
        <p className="text-lg font-black mb-1" data-testid="final-price">
          {pricing.monthly} {sym} {t("final.price_month")}
          <span className="text-xs font-medium text-white/80"> ({t("final.price_year")} {pricing.yearly} {sym})</span>
        </p>
        <p className="text-xs text-white/80 mb-6">
          {perDayCopy}
        </p>
        <button onClick={onStart} data-testid="lv2-final-cta"
          className="bg-white text-brand-600 font-black text-base px-8 py-4 rounded-full shadow-md active:scale-[0.98] transition inline-flex items-center gap-2">
          {t("final.cta")} <ChevronLeft className={`w-4 h-4 ${isRTL ? "" : "rotate-180"}`} />
        </button>
        <p className="text-[11px] text-white/80 mt-4">{t("final.guarantee")}</p>
      </div>
    </section>
  );
}

// ═════════════════════════════════════════════════════════════════════
// FOOTER — company info, legal, health disclaimer, login link
// ═════════════════════════════════════════════════════════════════════

function Footer() {
  const { t, lang, isRTL } = useLandingLang();
  const year = new Date().getFullYear();
  const CONTACT_EMAIL = "Support@calofay.com";
  const WHATSAPP_URL  = "https://wa.me/966504460629";
  // Blog + quiz links route to the language-appropriate track. English
  // blog posts arrive via the automation daily runner (2/day, same as
  // AR), so we always show the link — Google will index whatever exists.
  const blogPath = lang === "en" ? "/en/blog" : "/blog";

  return (
    <footer className="bg-neutral-950 text-white/80 pt-14 pb-8 px-4 relative overflow-hidden" dir={isRTL ? "rtl" : "ltr"} data-testid="lv2-footer">
      {/* Soft orange top glow to bridge from the CTA above */}
      <div
        className="absolute inset-x-0 top-0 h-40 -z-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 700px 200px at 50% 0%, rgba(249,115,22,0.20), transparent 70%)",
        }}
      />

      <div className="relative max-w-6xl mx-auto">
        {/* Top row: brand + quick sub CTA */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pb-10 border-b-2 border-white/10">
          <div>
            {lang === "en" ? (
              <div className="inline-flex items-center gap-2 select-none mb-4" data-testid="lv2-footer-logo">
                <img src="/calofay-mark.webp" alt="" style={{ height: 44, width: 44 }} className="rounded-[22%]" draggable={false} />
                <span className="font-black tracking-tight text-white" style={{ fontSize: 30, letterSpacing: "-0.02em" }}>Calofay</span>
              </div>
            ) : (
              <img
                src="/calofay-logo-dark.webp"
                alt="Calofay"
                className="h-12 md:h-14 w-auto mb-4 select-none"
                draggable={false}
                loading="lazy"
                data-testid="lv2-footer-logo"
              />
            )}
            <p className="text-sm text-white/60 leading-relaxed max-w-md">
              {t("footer.tagline")}
            </p>
          </div>

          <div className="flex flex-col md:items-end justify-between gap-4">
            <div className="text-sm text-white/70 md:text-left">
              <div className="text-[11px] font-bold text-white/50 mb-1">{t("footer.support")}</div>
              <a href={`mailto:${CONTACT_EMAIL}`} className="block hover:text-brand-400 transition" data-testid="lv2-footer-email">
                {CONTACT_EMAIL}
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 mt-1.5 text-emerald-400 hover:text-emerald-300 font-bold transition"
                 data-testid="lv2-footer-whatsapp">
                {/* Official WhatsApp glyph */}
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden>
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zM20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.892c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652a11.882 11.882 0 005.71 1.447h.006c6.585 0 11.946-5.336 11.949-11.896 0-3.176-1.24-6.165-3.495-8.407z"/>
                </svg>
                <span>{t("footer.whatsapp")}</span>
              </a>
            </div>
            <a href="/login" data-testid="lv2-footer-login"
               className="inline-flex items-center gap-2 bg-white text-neutral-900 font-bold text-sm px-5 py-2.5 rounded-full border-2 border-white hover:bg-brand-500 hover:text-white hover:border-brand-500 transition">
              {t("footer.login")} <ChevronLeft className={`w-4 h-4 ${isRTL ? "" : "rotate-180"}`} />
            </a>
          </div>
        </div>

        {/* Middle: link columns (3-col grid) */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 py-10 border-b-2 border-white/10">
          <div>
            <div className="text-xs font-black text-white mb-3">{t("footer.col_content")}</div>
            <ul className="space-y-2 text-sm text-white/70">
              <li><a href={blogPath} className="hover:text-brand-400 transition" data-testid="lv2-footer-blog">{t("footer.blog")}</a></li>
              <li><a href="#lv2-price" className="hover:text-brand-400 transition" data-testid="lv2-footer-price">{t("footer.pricing")}</a></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-black text-white mb-3">{t("footer.col_legal")}</div>
            <ul className="space-y-2 text-sm text-white/70">
              <li><a href="/terms"   className="hover:text-brand-400 transition" data-testid="lv2-footer-terms">{t("footer.terms")}</a></li>
              <li><a href="/privacy" className="hover:text-brand-400 transition" data-testid="lv2-footer-privacy">{t("footer.privacy")}</a></li>
              <li><a href="/health-disclaimer" className="hover:text-brand-400 transition" data-testid="lv2-footer-health">{t("footer.health")}</a></li>
              <li><a href="/methodology" className="hover:text-brand-400 transition">{t("footer.methodology")}</a></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-black text-white mb-3">{t("footer.col_refund")}</div>
            <ul className="space-y-2 text-xs text-white/70 leading-relaxed">
              <li>{t("footer.refund_web")}</li>
              <li>{t("footer.refund_ios")}</li>
            </ul>
          </div>
        </div>

        {/* Bottom: company + copyright */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 pt-8 text-[11px] text-white/50">
          <div className="space-y-1">
            <div>© {year} <b className="text-white">Evana Care LLC</b> — {t("footer.copyright")}</div>
            <div>30 North Gould Street, Sheridan, WY 82801 · United States</div>
          </div>
          <div className="flex items-center gap-3">
            <span>{t("footer.made_love")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ═════════════════════════════════════════════════════════════════════
// PAGE
// ═════════════════════════════════════════════════════════════════════

export default function LandingV2({ variant = "default" } = {}) {
  const nav = useNavigate();
  const location = useLocation();
  const pricing = usePricing();
  // Pull the global i18n handle so we can keep the app-wide language
  // context in sync with the landing's own `lang` state. Without this
  // the visitor could switch to English on `/en`, click "Try Calofay",
  // and see the Arabic quiz because `I18nProvider` initialised once at
  // app boot and ignored the local landing state.
  const globalI18n = useI18n();
  // Country-aware default detection — GCC/Arab countries default to
  // Arabic, everything else defaults to English. `null` = still loading,
  // in which case we fall back to sessionStorage → Arabic to avoid a
  // visible AR→EN flip after the geo call completes.
  const [geoDefault, setGeoDefault] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/public/geo")
      .then(({ data }) => {
        if (cancelled) return;
        const arCountries = ["SA", "AE", "KW", "QA", "BH", "OM", "EG", "JO", "LB", "SY", "IQ", "YE", "MA", "TN", "DZ", "LY", "SD", "MR", "PS"];
        setGeoDefault(arCountries.includes(data?.country) ? "ar" : "en");
      })
      .catch(() => { if (!cancelled) setGeoDefault("ar"); });
    return () => { cancelled = true; };
  }, []);
  // Lang resolution priority (highest → lowest):
  //   1. URL prefix `/en` or `/ar` (marketing campaign deep-links).
  //   2. sessionStorage — a visitor who toggled inside the tab keeps
  //      their choice across a hard refresh.
  //   3. geo-detected default (from /api/public/geo).
  //   4. Arabic — the app's native language.
  const [lang, setLang] = useState(() => {
    const path = (typeof window !== "undefined" && window.location.pathname) || "";
    if (path === "/en" || path.startsWith("/en/")) return "en";
    if (path === "/ar" || path.startsWith("/ar/")) return "ar";
    try {
      const s = sessionStorage.getItem("calofay:landing:lang");
      if (s === "en" || s === "ar") return s;
    } catch {}
    return "ar";  // safe boot default; geo will upgrade to EN below if needed
  });
  const isRTL = lang === "ar";
  // Once the geo probe returns, upgrade the lang IF the visitor hasn't
  // already made an explicit choice (URL prefix or session pick). The
  // first-paint flash from AR→EN is acceptable — better than serving
  // AR to a US visitor who'll never engage.
  useEffect(() => {
    if (geoDefault == null) return;
    const path = window.location.pathname;
    if (path === "/en" || path.startsWith("/en/") || path === "/ar" || path.startsWith("/ar/")) return;
    let picked = false;
    try { picked = !!sessionStorage.getItem("calofay:landing:lang"); } catch {}
    if (picked) return;
    if (geoDefault !== lang) setLang(geoDefault);
  }, [geoDefault]); // eslint-disable-line react-hooks/exhaustive-deps
  // t() looks up a key in COPY and returns the string for the current
  // lang, falling back to Arabic if the English translation is missing
  // (Phase 2 sections). If neither exists we return the key itself so
  // a typo is loud but non-fatal.
  const t = (key) => {
    // Variant-specific overrides: any `key` may have a paired
    // `${key}__${variant}` in COPY that wins when the current landing
    // instance is running under that variant. Falls back to the plain
    // key when no variant-specific entry exists, so most COPY entries
    // don't need duplication.
    if (variant && variant !== "default") {
      const scoped = COPY[`${key}__${variant}`];
      if (scoped) return scoped[lang] ?? scoped.ar ?? key;
    }
    const entry = COPY[key];
    if (!entry) return key;
    return entry[lang] ?? entry.ar ?? key;
  };
  const changeLang = (newLang) => {
    if (newLang !== "ar" && newLang !== "en") return;
    setLang(newLang);
    try { sessionStorage.setItem("calofay:landing:lang", newLang); } catch {}
    // Keep the URL in sync with the toggle so the current page is
    // shareable and refreshing keeps the language. `/en` and `/ar`
    // both mount the same landing so this is a no-op navigation for
    // React beyond updating the visible URL.
    const target = newLang === "en" ? "/en" : "/ar";
    if (location.pathname !== target) {
      nav(target, { replace: true });
    }
  };
  // Force the html lang/dir every time the landing lang changes — this
  // both (a) enables the correct global CSS rules (e.g. font stack,
  // numeric tabular direction) and (b) makes the landing immune to the
  // global i18n provider which would otherwise flip us back based on
  // the anon-auth default. On unmount we let the global i18n provider
  // reclaim ownership as the user navigates elsewhere in the app.
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    // hreflang link tags — tell Google there are two language versions
    // of this page so it can serve the right URL by region and never
    // treat AR/EN as duplicate content. We refresh on every lang flip
    // in case the visitor toggles; the pair (ar + en + x-default)
    // stays constant.
    const ensureLink = (rel, hreflang, href) => {
      let el = document.querySelector(`link[rel="${rel}"][hreflang="${hreflang}"]`);
      if (!el) {
        el = document.createElement("link");
        el.rel = rel;
        el.hreflang = hreflang;
        document.head.appendChild(el);
      }
      el.href = href;
    };
    ensureLink("alternate", "ar",        "https://calofay.app/ar");
    ensureLink("alternate", "en",        "https://calofay.app/en");
    ensureLink("alternate", "x-default", "https://calofay.app/");
    // Canonical — the currently-visible language wins.
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.rel = "canonical";
      document.head.appendChild(canon);
    }
    canon.href = lang === "en" ? "https://calofay.app/en" : "https://calofay.app/";
  }, [lang, isRTL]);
  // Seed the public web-only promo code `NEW10` into the shared affiliate
  // slot so Checkout.jsx auto-applies it when the visitor arrives at
  // /checkout. We DON'T overwrite an existing code — a real affiliate
  // referral (set by RedeemRedirect on /join/:code visits) always wins.
  // This runs once per visit; subsequent visits with the code already
  // seeded are no-ops.
  useEffect(() => {
    if (isNativeApp()) return;
    try {
      const existing = localStorage.getItem("calofay_affiliate_code");
      if (!existing) localStorage.setItem("calofay_affiliate_code", "NEW10");
    } catch {}
    // A/B variant assignment is deferred to the CTA click (see
    // `onStart`) so the experiment sample size equals the number
    // of people who actually interacted with the subscribe button,
    // not every random bouncer who hit the landing page.
    // Harvest click IDs + UTMs + Meta cookies from the URL on EVERY
    // landing-page mount. First-touch wins per field (see attribution.js)
    // so navigating to /quiz → /checkout and back doesn't clobber the
    // original ad click. Persisted in localStorage with a 90-day TTL —
    // the same window Meta's CAPI uses for attribution matching.
    try { captureAttribution(); } catch {}
  }, []);
  // Seed the picked language for the quiz/onboarding downstream so
  // the visitor doesn't get re-asked their language on the next screen.
  // Route branches by platform: native iOS users hit the walkthrough
  // (`/ios/welcome`) which then flows into signup+quiz; web visitors
  // go straight into the quiz so we don't force them through an
  // iOS-only onboarding narrative that ends by asking them to
  // download the app.
  // Push the landing lang into the global I18n context every time it
  // flips, so downstream screens (Quiz, Checkout, Activate, PaymentSuccess,
  // Results, and any authenticated app screen) all render in the same
  // language the visitor picked here — with no double-flash and no
  // reliance on manual localStorage reads on each mount.
  useEffect(() => {
    if (!globalI18n) return;
    if (globalI18n.lang !== lang && (lang === "ar" || lang === "en")) {
      globalI18n.setLang(lang);
    }
  }, [lang, globalI18n]);
  // Web: Gulf-country welcome (SA/AE/QA/KW/OM) is triggered from the
  // subscribe CTA — not on landing load. First-visit Gulf visitors
  // see the landing, tap "اشترك الآن" / "Try Calofay", then the
  // premium country splash, then the quiz. On subsequent clicks
  // (already shown once) the overlay is skipped entirely.
  const [gulfWelcomeVisible, setGulfWelcomeVisible] = useState(false);
  const finishGulfWelcome = () => {
    setGulfWelcomeVisible(false);
    // Continue to the destination we were going to before the splash.
    if (isNativeApp()) { nav(START_ROUTE); return; }
    // A/B test — variant B skips the quiz entirely and heads to the
    // paywall. Variant A keeps the original quiz-first flow.
    if (abGetVariant() === "B") { nav("/checkout"); return; }
    nav(variant === "recipe" ? "/recipe/quiz" : "/quiz");
  };

  const onStart = async () => {
    try { localStorage.setItem("calofay:lang", lang); } catch {}
    // Belt-and-suspenders: force the global provider to the picked
    // language before we navigate away so the next screen paints in
    // the right locale on the first frame, not the second.
    if (globalI18n && globalI18n.lang !== lang) globalI18n.setLang(lang);
    // Native: straight to the walkthrough (IAP-driven, no web splash).
    // The A/B test is web-only — no bucketing on native.
    if (isNativeApp()) { nav(START_ROUTE); return; }
    // GTM: fire GA4 `add_to_cart` on any subscribe CTA tap.
    import("@/lib/gtm").then((m) => m.pushAddToCart()).catch(() => {});
    // A/B bucketing happens HERE — at the moment of CTA click — so
    // that `unique_clicks` ≡ `unique_assignments` per variant. The
    // ensureVariant call is idempotent per visitor_id and sticky
    // forever, so a repeat CTA tap by the same browser never
    // flips the variant. The subsequent `abTrackClick()` is deduped
    // server-side by (visitor_id, variant, event_type) so a visitor
    // who taps CTA 10 times still counts as 1 unique click.
    let resolvedVariant = "A";
    try { resolvedVariant = await abEnsureVariant(); } catch {}
    abTrackClick();
    // Intercept for first-visit Gulf visitors — show the welcome
    // splash BEFORE the next screen. `shouldShowCountryWelcome`
    // already gates on both "in Gulf" and "not seen before" so this is
    // a no-op for everyone else. The splash's "continue" handler
    // routes variant A→/quiz and variant B→/checkout.
    if (shouldShowCountryWelcome()) {
      setGulfWelcomeVisible(true);
      return;
    }
    // No splash — go straight to the variant's destination.
    if (resolvedVariant === "B") { nav("/checkout"); return; }
    nav(variant === "recipe" ? "/recipe/quiz" : "/quiz");
  };
  return (
    <LandingLangContext.Provider value={{ lang, setLang: changeLang, t, isRTL, variant }}>
      {/* Gulf-country welcome splash — shown ONCE per install, only
          after the user taps a subscribe CTA (not on landing load).
          Non-Gulf and repeat visitors never mount this component. */}
      {gulfWelcomeVisible && (
        <CountryWelcome langOverride={lang} onContinue={finishGulfWelcome} />
      )}
      <div className="always-light min-h-screen bg-white text-neutral-900" dir={isRTL ? "rtl" : "ltr"} lang={lang} data-testid="lv2-root">
        <Header onStart={onStart} />
        <Hero onStart={onStart} variant={variant} />
        <ProblemSolution onStart={onStart} />
        <FeaturesHub onStart={onStart} />
        <HowToStart onStart={onStart} />
        <GuaranteeSection onStart={onStart} />
        {/* Customer testimonials — sits right below the guarantee so
            the trust story flows: refund promise → real reviews → price. */}
        <TestimonialsSection lang={lang} />
        <PriceValue onStart={onStart} pricing={pricing} />
        {/* Competitor comparison hidden on the recipe funnel — MyFitnessPal
            and Lose It don't ship a recipe library or IG/TikTok imports
            so the row-by-row table is misleading in that context. */}
        {variant !== "recipe" && <CompetitorsSection onStart={onStart} />}
        <FAQ pricing={pricing} />
        <FinalCta onStart={onStart} pricing={pricing} />
        <Footer />
        <WhatsAppFAB />
      </div>
    </LandingLangContext.Provider>
  );
}

// Floating WhatsApp support button — sits fixed at the visual
// bottom-right on every LandingV2 view. Brand-orange (not the
// traditional WhatsApp green) so it belongs to Calofay's identity
// while still being unmistakably a chat action. Includes a subtle
// pulsing ring, a hover tooltip, and a slide-in mount animation to
// draw the eye without shouting.
function WhatsAppFAB() {
  // Same number the footer already uses — kept as a constant here so
  // moving to a config/env later is a one-line change.
  const WHATSAPP_URL =
    "https://wa.me/966504460629?text=" +
    encodeURIComponent("عندي سؤال عن تطبيق كالوفاي");

  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="تواصل معنا عبر واتساب"
      data-testid="whatsapp-fab"
      className="group fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[60]"
      onClick={() => track && track("whatsapp_fab_click")}
    >
      {/* The button itself — pulsing ring removed per user preference (calmer, non-attention-grabbing) */}
      <span
        className="relative flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full shadow-2xl transition-transform duration-200 group-hover:scale-110 group-active:scale-95"
        style={{
          background: "#25D366",
          boxShadow:
            "0 12px 30px -6px rgba(37,211,102,0.55), 0 0 0 3px rgba(255,255,255,0.9) inset",
        }}
      >
        {/* Official WhatsApp glyph — white on WhatsApp green */}
        <svg
          viewBox="0 0 32 32"
          className="w-7 h-7 sm:w-8 sm:h-8 text-white"
          fill="currentColor"
          aria-hidden
        >
          <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977.86 2.78 1.017 2.13 3.03 4.153 5.13 5.257.945.501 2.13 1.005 3.198 1.076.171 0 1.318-.088 1.318-.315v-1.99c0-.316-1.017-.516-1.302-.816zM16.32 30h-.033a13.933 13.933 0 0 1-7.079-1.924L2 30l1.99-7.14A13.94 13.94 0 0 1 1.94 15.8C1.94 8.132 8.36 2 16.32 2c3.868 0 7.5 1.492 10.24 4.192a13.909 13.909 0 0 1 4.242 9.916c0 7.667-6.42 13.892-14.482 13.892zm-.033-1.977c6.702 0 12.245-5.297 12.245-11.915 0-3.196-1.243-6.198-3.5-8.457a11.938 11.938 0 0 0-8.746-3.575c-6.702 0-12.108 5.297-12.108 11.999a11.79 11.79 0 0 0 1.837 6.358l.286.458-1.187 4.266 4.4-1.158.443.257c1.809 1.076 3.898 1.767 6.33 1.767z" />
        </svg>
      </span>
      {/* Hover tooltip — inline label on desktop, hidden on touch */}
      <span
        className="hidden md:block absolute right-full top-1/2 -translate-y-1/2 mr-3 px-3 py-1.5 rounded-full text-sm font-bold text-white opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity duration-200"
        style={{
          background: "rgba(17,24,39,0.92)",
          boxShadow: "0 6px 16px -4px rgba(0,0,0,0.35)",
        }}
      >
        تواصل معنا واتساب
      </span>
    </a>
  );
}
